import { ChangeDetectionStrategy, Component, inject, signal, OnInit } from '@angular/core';

import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FuseCardComponent } from '../../../../@fuse/components/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import type { Entry, OptionsItem, Taxonomy } from '../../../features/admin/modules/modules.model';
import { TaxonomyOptionFilterModeEnum } from '../../../features/admin/modules/modules.model';
import { INPUT_OPTIONS, UPLOAD_FIELD_TYPES } from './field-config';
import { AUTOCOMPLETE_TYPES, deriveAutocomplete } from './extensions/autocomplete.extension';
import { TAXONOMY_COLOR_ROLES } from '../../utils/taxonomy-color';
import {
  BASED_ON_SELECTED_FIELD,
  createFieldWithOverrides,
  OPERATOR_SELECT_FIELD,
} from '../../utils/formly-field.util';
import { cloneDeep } from 'lodash-es';
import { UtilityService } from '../../services/utility.service';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  forkJoin,
  map,
  of,
  pairwise,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { FiltersService } from '../../../features/admin/modules/features/filters/filters.service';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { UserRolesStore } from '../../../features/admin/user-roles/user-roles.store';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FieldProcessorService } from './services/field-processor.service';
import { ConditionFieldsService } from './services/conditions-field.service';
import { ValueMappingFieldService } from './services/mapping-field.service';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { ApiMappingsStore } from '../../../features/admin/modules/features/api-mappings/api-mapping.store';
import { resolveRdwFieldMeta } from './rdw.types';
import { resolveKvkFieldMeta } from './kvk.types';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FieldReferenceService } from 'app/features/admin/modules/features/form-builder/services/field-reference.service';
import { ActivatedRoute } from '@angular/router';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import type { FieldOption } from '../../../features/admin/modules/features/form-builder/form.model';
import { OwnViewFieldType } from './readonly/own-view-field.type';
import { SEARCH_MIN_ROWS } from '../../utils/search-visibility.util';

@Component({
  selector: 'app-formly-form-builder',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, FormsModule, FuseCardComponent, MatProgressSpinner, ReactiveFormsModule, CdkDropListGroup],
  template: `
    @if (field.props.label) {
      <h3>{{ field.props.label }}</h3>
    }
    @if (moduleId() !== undefined) {
      <div class="w-full">
        <form [formGroup]="formBuilderForm">
          @defer {
            <!-- The read-mode stamp never reaches this NESTED formly-form (its own control tree),
                 so the drag group asks the shared gate itself (canMutate on OwnViewFieldType). -->
            <formly-form
              cdkDropListGroup
              [cdkDropListGroupDisabled]="!canMutate()"
              [form]="formBuilderForm"
              [fields]="fields"
              [model]="formBuilderModel"
              [options]="formBuilderOptions"></formly-form>
          } @placeholder {
            <fuse-card class="border text-center mt-4 p-4 mb-4">
              <div class="text-center w-full">
                <mat-spinner class="m-auto mt-4 mb-8"></mat-spinner>
                <h3>Laden van formuliergegevens</h3>
              </div>
            </fuse-card>
          } @error {
            <div class="text-center p-4">
              <p class="text-warn">Er is een fout opgetreden bij het laden van dit onderdeel.</p>
            </div>
          }
        </form>
      </div>
    }
  `,
})
export class FormlyFormBuilderComponent extends OwnViewFieldType implements OnInit {
  apiMappingsStore = inject(ApiMappingsStore);
  valueMappingFieldService = inject(ValueMappingFieldService);
  conditionFieldsService = inject(ConditionFieldsService);
  fieldReferenceService = inject(FieldReferenceService);
  modulesStore = inject(ModulesStore);
  utilityService = inject(UtilityService);
  fieldProcessorService = inject(FieldProcessorService);
  entriesService = inject(EntriesService);
  filtersService = inject(FiltersService);
  formService = inject(FormService);
  userRolesStore = inject(UserRolesStore);
  route = inject(ActivatedRoute);
  fuseConfirmationService = inject(FuseConfirmationService);
  fieldTypes = INPUT_OPTIONS;
  generatedForms = this.modulesStore.generatedForms;
  tempEntries = {};

  moduleId = signal(undefined);
  module = signal(undefined);
  entry = signal(undefined);

  formBuilderForm = new FormGroup({});

  conditionalDisabledFields;
  conditionalHideFields;
  nextButtonHiddenFields;

  formBuilderField; // set afterviewinit
  fieldsWithoutRepeater; // set afterviewinit

  fields: FormlyFieldConfig[];
  originalFieldTypes = new Map<string, string[]>();

  generatedForm: FormlyFieldConfig[] = [];
  formBuilderModel = {};
  formBuilderOptions;

  previousFields = null; // Store previous fields state
  pendingRestoration = false; // Flag to prevent multiple dialogs

  formBUilderChanges: Subscription;

  // Guard against duplicate valueChanges subscriptions when formly re-runs
  // hooks.onInit for the same control on an options.build() rebuild. Keyed on
  // the control (not a model flag): duplicate() deep-clones the model including
  // any flags, while formly creates a fresh control for the new item.
  readonly #typeNameSyncedControls = new WeakSet<AbstractControl>();
  readonly #moduleCodeSyncedControls = new WeakSet<AbstractControl>();

  private destroy$ = injectDestroy();

  constructor() {
    super();
  }

  ngOnInit() {
    // paramMap emits synchronously on subscribe, so this single subscription also
    // performs the initial build — a separate init block would run everything twice.
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.entry.set(this.options.formState?.entry);
      this.moduleId.set(this.options.formState?.entity?.id);
      this.module.set(this.options.formState?.entity);
      this.formBuilderModel = { fields: this.field.formControl.value };
      this.formBuilderOptions = this.options;
      this.setField();
      this.setFields();
      this.fetchRequiredEntries();
      if (this.field.formControl.value != null && this.field.formControl.value != '') {
        this.generatedForm = this.formService.generateForm(this.field.formControl.value);
      }

      this.setupFormChangeDetection();
    });
  }

  // Extract form change detection to a separate method for clarity
  private setupFormChangeDetection() {
    this.formBUilderChanges?.unsubscribe();
    this.formBUilderChanges = this.formBuilderForm.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged(), debounceTime(300))
      .subscribe((value: any) => {
        // Skip processing during restoration
        if (this.pendingRestoration) return;

        this.detectDeletedFields(value.fields);

        // Only update if not restoring
        if (!this.pendingRestoration) {
          const fieldsValue = this.formBuilderForm.getRawValue()['fields'];
          this.generatedForm = this.formService.generateForm(fieldsValue);
          if (JSON.stringify(this.field.formControl.value) !== JSON.stringify(fieldsValue)) {
            this.field.formControl.patchValue(fieldsValue);
          }
        }
      });
  }

  // Keeps the hidden `typeName` control in sync with `type` from hooks instead of a
  // hide-expression: patching inside an expression re-arms the debounced regenerate
  // pipeline on every expression sweep (checkExpressionOn: 'modelChange'), feeding
  // itself. emitEvent stays false — `type` already emitted; the model is mutated
  // directly because formly only syncs control -> model on emitted valueChanges.
  // Returns true when persisted drift was corrected (control or model changed).
  private syncTypeName(field: FormlyFieldConfig): boolean {
    const typeNameControl = field.form?.get('typeName');
    if (!typeNameControl) return false;
    const label = this.formService.convertFormlyTypeToLabel(field.model?.type);
    let changed = false;
    if (typeNameControl.value !== label) {
      typeNameControl.patchValue(label, { emitEvent: false });
      changed = true;
    }
    if (field.model && field.model.typeName !== label) {
      field.model.typeName = label;
      changed = true;
    }
    return changed;
  }

  // Only reset moduleCode for `manual` mode — manual options always live on the
  // current module so the local code is the right answer. Taxonomy options can
  // legitimately come from another module (cross-module taxonomy reference,
  // resolved via moduleCodeMapper in form.service.ts:1287); resetting on taxonomy
  // clobbers that and breaks the option lookup at runtime. Runs from hooks (not a
  // hide-expression) for the same self-feeding-pipeline reason as syncTypeName.
  // Formly builds all sibling controls before any hooks.onInit runs, so the
  // moduleCode control is guaranteed to exist when the optionsType hook fires.
  // Returns true when persisted drift was corrected (control or model changed).
  private syncManualModuleCode(field: FormlyFieldConfig): boolean {
    if (field.form?.get('optionsType')?.value !== 'manual') return false;
    const moduleCode = this.module()?.code;
    const moduleCodeControl = field.form?.get('moduleCode');
    if (moduleCode == null || !moduleCodeControl) return false;
    let changed = false;
    if (moduleCodeControl.value !== moduleCode) {
      moduleCodeControl.patchValue(moduleCode, { emitEvent: false });
      changed = true;
    }
    if (field.model && field.model.moduleCode !== moduleCode) {
      field.model.moduleCode = moduleCode;
      changed = true;
    }
    return changed;
  }

  // One controlled pipeline pass after an init-time sync corrected persisted
  // drift (stale typeName/moduleCode in a saved definition): the syncs patch with
  // emitEvent:false, so without this the outer sourceForm control would only be
  // normalized on the next real edit. Deduped per microtask; the debounced
  // pipeline's stringify-guard prevents any feedback loop.
  #driftReconcileQueued = false;
  private queueDriftReconcile(): void {
    if (this.#driftReconcileQueued) return;
    this.#driftReconcileQueued = true;
    queueMicrotask(() => {
      this.#driftReconcileQueued = false;
      this.formBuilderForm.updateValueAndValidity();
    });
  }

  detectDeletedFields(currentFields) {
    // Skip if restoration is pending
    if (this.pendingRestoration) {
      return;
    }

    // First time initialization
    if (!this.previousFields) {
      this.previousFields = JSON.parse(JSON.stringify(currentFields));
      return;
    }

    const getFieldLookup = () => {
      const lookup = new Map();

      if (this.options?.formState?.entity?.generatedForm) {
        const generatedFields = this.options.formState.entity.generatedForm;

        // Recursive function to process fields at any nesting level
        const processField = field => {
          if (field && field.id) {
            lookup.set(field.id, field);
          }

          // Process field groups (tabs structure)
          if (field.fieldGroup) {
            field.fieldGroup.forEach(subField => {
              processField(subField);

              // Handle nested fieldGroups within tabs
              if (subField.fieldGroup) {
                subField.fieldGroup.forEach(f => processField(f));
              }
            });
          }

          // Process field arrays
          if (field.fieldArray && field.fieldArray.fieldGroup) {
            field.fieldArray.fieldGroup.forEach(f => processField(f));
          }

          // Handle other nested field types
          if (field.tabsFields) field.tabsFields.forEach(processField);
          if (field.repeatFields) field.repeatFields.forEach(processField);
          if (field.groupFields) field.groupFields.forEach(processField);
          if (field.accordionFields) field.accordionFields.forEach(processField);
          if (field.pagesFields) field.pagesFields.forEach(processField);
          if (field.wizardFields) field.wizardFields.forEach(processField);
        };

        // Start processing from top-level fields
        generatedFields.forEach(field => processField(field));
      }

      return lookup;
    };

    const fieldLookup = getFieldLookup();

    // Build maps of field IDs for quick lookup
    const getFieldsById = fields => {
      const result = new Map();

      const processField = field => {
        if (field && field.id) {
          // Look up the corresponding field with keyPath
          const generatedField = fieldLookup.get(field.id);
          result.set(field.id, {
            isOriginal: field.isOriginalField !== false,
            label: field.label || 'Onbekend veld',
            key: field.key || generatedField?.key,
            keyPath: generatedField?.props?.keyPath,
          });
        }

        if (field) {
          if (Array.isArray(field.tabsFields)) field.tabsFields.forEach(processField);
          if (Array.isArray(field.repeatFields)) field.repeatFields.forEach(processField);
          if (Array.isArray(field.groupFields)) field.groupFields.forEach(processField);
          if (Array.isArray(field.accordionFields)) field.accordionFields.forEach(processField);
          if (Array.isArray(field.pagesFields)) field.pagesFields.forEach(processField);
          if (Array.isArray(field.wizardFields)) field.wizardFields.forEach(processField);
        }
      };

      if (Array.isArray(fields)) {
        fields.forEach(processField);
      }

      return result;
    };

    const previousFieldsById = getFieldsById(this.previousFields);
    const currentFieldsById = getFieldsById(currentFields);

    // Find deleted original fields
    const deletedOriginalFields = [];
    previousFieldsById.forEach((info, id) => {
      if (!currentFieldsById.has(id)) {
        deletedOriginalFields.push({
          id,
          label: info.label,
          key: info.key,
          keyPath: info.keyPath,
        });
      }
    });

    if (deletedOriginalFields.length > 0) {
      const deletedField = deletedOriginalFields[0];

      // Set flag before any operations
      this.pendingRestoration = true;

      // Check for references
      let message = `Let op: het verwijderen van het veld "${deletedField.label}" kan leiden tot dataverlies. Wilt u doorgaan?`;
      let title = 'Verwijderen van veld';

      const referenceCheck = this.fieldReferenceService.checkFieldReferencesByKeyPath(
        this.moduleId(),
        deletedField.keyPath
      );

      if (referenceCheck.referenced) {
        // Start the warning message with HTML formatting
        let referenceText = `<p>Het verwijderen van dit veld kan leiden tot fouten in deze onderdelen. Het veld wordt gebruikt in de volgende onderdelen:</p>`;

        // Create an unordered list of references
        referenceText += '<ul>';
        referenceCheck.references.forEach(ref => {
          referenceText += `<li>${ref.location}</li>`;
        });
        referenceText += '</ul><br>';

        referenceText += '<p>Wilt u doorgaan met verwijderen?</p>';

        title = `Het invoerveld "${deletedField.label}" heeft referenties'`;
        message = referenceText;
      }

      if (!referenceCheck.referenced) {
        this.pendingRestoration = false;
        return;
      }

      const confirmation = this.fuseConfirmationService.open({
        title: title,
        message: message,
        icon: {
          show: true,
          name: 'heroicons_outline:exclamation-triangle',
          color: 'warn',
        },
        actions: {
          cancel: {
            label: 'Annuleren',
          },
          confirm: {
            label: 'Invoerveld verwijderen',
            color: 'warn',
          },
        },
        dismissible: false,
      });

      confirmation
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result !== 'confirmed') {
            const previousFieldsCopy = JSON.parse(JSON.stringify(this.previousFields));

            // Create a new form instance completely detached from change detection
            setTimeout(() => {
              try {
                this.field.formControl.patchValue(previousFieldsCopy);
                this.formBuilderModel = { fields: previousFieldsCopy };
                this.formBuilderForm.patchValue({ fields: previousFieldsCopy }, { emitEvent: false });
                this.generatedForm = this.formService.generateForm(previousFieldsCopy);

                // Delay resetting the flag to allow Angular to process everything
                setTimeout(() => {
                  this.pendingRestoration = false;
                }, 500);
              } catch (error) {
                this.pendingRestoration = false;
              }
            }, 200);
          } else {
            // User confirmed deletion, update previous fields state
            this.previousFields = JSON.parse(JSON.stringify(currentFields));
            this.pendingRestoration = false;
          }
        });
    } else {
      // No fields deleted, update state
      this.previousFields = JSON.parse(JSON.stringify(currentFields));
    }
  }

  setAvaibleFieldTypeOptions() {
    let options = UtilityService.sortArrayByProperty([...this.fieldTypes], 'group');

    if (this.field.props.formbuilderSettings === 'basic') {
      options = this.fieldTypes.filter(fieldType => {
        return fieldType.level === 'basic';
      });
    }

    return options;
  }

  setField() {
    this.conditionalDisabledFields = [
      {
        key: 'conditionalDisable',
        className: 'inline-block mt-4 w-full',
        type: 'toggle',
        props: {
          label: 'Conditioneel uitschakelen',
        },
      },
      createFieldWithOverrides(this.conditionFieldsService.getCondtionsField(this.module(), 'interface'), {
        key: 'conditionsDisable',
        type: 'repeat',
        props: {
          label: 'Voorwaardes',
          seperatorText: 'EN',
          addText: 'Conditie toevoegen',
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            return !field.parent.form.get('conditionalDisable')?.value;
          },
        },
      }),
    ];

    this.conditionalHideFields = [
      {
        key: 'conditional',
        className: 'inline-block mt-4 w-full',
        type: 'toggle',
        props: {
          label: 'Conditioneel weergeven',
        },
      },
      createFieldWithOverrides(this.conditionFieldsService.getCondtionsField(this.module(), 'interface'), {
        key: 'conditions',
        type: 'repeat',
        props: {
          label: 'Voorwaardes',
          seperatorText: 'EN',
          addText: 'Conditie toevoegen',
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            return !field.parent.form.get('conditional').value;
          },
        },
      }),
    ];

    this.nextButtonHiddenFields = [
      {
        key: 'nextButtonConditional',
        className: 'inline-block mt-4 w-full',
        type: 'toggle',
        props: {
          label: "Conditioneel 'Volgende stap' knop verbergen",
        },
      },
      this.#createNextButtonConditionsField(),
    ];

    this.formBuilderField = {
      type: 'tabs',
      // preserveContent: rebuilding the formly subtree on every tab switch is the
      // dominant cost inside an expanded field panel. persistSelectedTab: these
      // runtime settings-tabs get a regenerated formly id every build, so a stored
      // tab index is never read back — persisting it only spams the preference API.
      props: { preserveContent: true, persistSelectedTab: false },
      fieldGroup: [
        {
          props: { label: 'Algemeen' },
          fieldGroup: [
            {
              key: 'type',
              type: 'autocomplete',
              defaultValue: 'input',
              props: {
                label: 'Veldtype',
                required: true,
                requireSelection: true,
                options: this.setAvaibleFieldTypeOptions(),
              },
              hooks: {
                onInit: (field: FormlyFieldConfig) => {
                  // Runs on every (re)build; equality-guarded so it stays cheap.
                  // A correction here means the SAVED definition drifted — push it
                  // through to the outer control instead of waiting for an edit.
                  if (this.syncTypeName(field)) {
                    this.queueDriftReconcile();
                  }

                  // Control-keyed guard, NOT the _initialized model flag below:
                  // duplicate() clones the model with _initialized=true while the
                  // duplicated item gets a fresh control that still needs this sync.
                  if (field.formControl && !this.#typeNameSyncedControls.has(field.formControl)) {
                    this.#typeNameSyncedControls.add(field.formControl);
                    field.formControl.valueChanges
                      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
                      .subscribe(() => this.syncTypeName(field));
                  }

                  if (field.model._initialized) return;
                  field.model._initialized = true;

                  const originalType = field.model.type;
                  field.model._originalType = originalType;
                  let openDialog = true;

                  field.formControl.valueChanges
                    .pipe(distinctUntilChanged(), pairwise(), takeUntil(this.destroy$))
                    .subscribe(([oldType, newType]) => {
                      const isOriginalField = field.model?.isOriginalField === true;

                      if (isOriginalField && oldType !== newType) {
                        const newTypeInfo = this.fieldTypes.find(type => type.value === newType);
                        const oldTypeInfo = this.fieldTypes.find(type => type.value === oldType);

                        if (newTypeInfo && oldTypeInfo) {
                          if (openDialog) {
                            openDialog = false;
                            const confirmation = this.fuseConfirmationService.open({
                              title: 'Wijzigen van datatype',
                              message: 'Let op: het wijzigen van het datatype kan leiden tot dataverlies.',
                              actions: {
                                cancel: {
                                  label: 'Annuleren',
                                },
                                confirm: {
                                  label: 'Doorgaan',
                                },
                              },
                            });

                            confirmation
                              .afterClosed()
                              .pipe(first())
                              .subscribe(result => {
                                if (result !== 'confirmed') {
                                  field.formControl.setValue(oldType, { emitEvent: true });
                                }
                              });
                          }
                        }
                      }
                    });
                },
              },
            },
            {
              key: 'id',
              type: 'input',
              className: 'hidden',
              props: {
                label: 'id',
                required: true,
              },
              hooks: {
                onInit: (field: FormlyFieldConfig) => {
                  if (field.form?.controls['id'].value === undefined || field.form?.controls['id'].value === null) {
                    field.form?.controls['id'].patchValue(field.id);
                  }
                },
              },
            },
            {
              key: 'label',
              type: 'input',
              props: {
                label: 'Veldlabel',
                description: 'Dit is de naam die op de bewerkpagina zal verschijnen',
                placeholder: '',
                required: true,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.hasSubFields &&
                    selectedFieldType?.value !== 'group' &&
                    selectedFieldType?.value !== 'accordion'
                  );
                },
              },
            },
            {
              key: 'isOriginalField',
              type: 'toggle',
              defaultValue: false,
              className: 'hidden',
              props: {
                label: 'Opgeslagen in database',
              },
            },
            {
              key: 'text',
              type: 'textarea',
              props: {
                label: 'Tekst',
                description: '',
                rows: 6,
                placeholder: '',
                required: true,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'text';
                },
              },
            },
            {
              key: 'interfaceActionModuleCode',
              type: 'custom-select',
              props: {
                label: 'Module voor interface acties',
                description: 'Selecteer de module waar de interface acties vandaan komen',
                required: true,
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'interface-action-button';
                },
                'props.options': () => {
                  return this.modulesStore.enabledModules().map(module => ({
                    value: module.code,
                    label: module.title,
                  }));
                },
              },
              hooks: {
                onInit: (field: FormlyFieldConfig) => {
                  // Set default value to current module if not already set
                  if (field.formControl && !field.formControl.value && this.module()?.code) {
                    field.formControl.setValue(this.module().code);
                  }
                },
              },
            },
            {
              key: 'interfaceActionId',
              type: 'custom-select',
              props: {
                label: 'Interface actie',
                description: 'Selecteer de interface actie die uitgevoerd moet worden',
                required: true,
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'interface-action-button';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  // Get the selected module code from the sibling field
                  const moduleCode = field.model?.interfaceActionModuleCode;
                  if (!moduleCode) return [];

                  const selectedModule = this.modulesStore.enabledModules().find(m => m.code === moduleCode);
                  if (!selectedModule?.interfaceActions) return [];

                  // Filter to only show actions with 'Formulier button' display location
                  return selectedModule.interfaceActions
                    .filter(action => action.event?.type?.includes('Formulier button'))
                    .map(action => ({
                      value: action.id,
                      label: action.name,
                    }));
                },
              },
            },
            {
              // TODO remove this before production
              key: 'key',
              type: 'input',
              props: {
                label: 'Key',
                description: 'Op basis van deze key wordt een waarde in de database gezet',
                placeholder: '',
                required: true,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);

                  if (this.hideIfBasisBuilder()) {
                    return true;
                  }

                  return (
                    selectedFieldType?.hasSubFields &&
                    selectedFieldType?.value !== 'group' &&
                    selectedFieldType?.value !== 'accordion'
                  );
                },
                'props.disabled': (field: FormlyFieldConfig) => field.model?.isOriginalField === true,
                'props.description': (field: FormlyFieldConfig) => {
                  if (field.model?.isOriginalField === true) {
                    return 'Dit veld is al in gebruik in de database en kan niet worden gewijzigd om dataverlies te voorkomen';
                  }
                  return 'Op basis van deze key wordt een waarde in de database gezet';
                },
              },
            },
            {
              // TODO remove this before production
              key: 'lock',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Veld locken',
                description: 'Met dit veld kan je invoervelden locken voor in de back-end',
                placeholder: '',
                required: false,
              },
              hide: this.field.props.formbuilderSettings === 'basic',
            },
            {
              key: 'hideDateOffsetType',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Offset type verbergen',
                info: "Verberg het type van de offset (bijv. 'Van tevoren' of 'achteraf')",
                description: '',
                placeholder: '',
                required: false,
              },
              hide: field => {
                if (this.field.props.formbuilderSettings === 'basic') {
                  return true;
                }

                const selectedFieldType = this.findSelectedFieldType(field);
                return selectedFieldType?.value !== 'date_offset';
              },
            },
            {
              // TODO remove this before production
              key: 'formbuilderSettings',
              type: 'custom-select',
              defaultValue: 'basic',
              props: {
                label: 'Formulier bouwer instellingen',
                description: '',
                placeholder: '',
                required: false,
                options: [
                  { label: 'Basis', value: 'basic' },
                  { label: 'Geavanceerd', value: 'advanced' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'form-builder';
                },
              },
            },

            {
              key: 'useCurrentDate',
              type: 'toggle',
              className: 'inline-block mt-4',
              props: {
                label: 'Gebruik huidige datum en tijd',
                placeholder: '',
                required: false,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'datetime' &&
                    selectedFieldType?.value !== 'datepicker_custom' &&
                    selectedFieldType?.value !== 'date_range'
                  );
                },
              },
            },
            {
              key: 'showOnlyTime',
              type: 'toggle',
              className: 'inline-block mt-4 w-full',
              defaultValue: false,
              props: {
                label: 'Alleen tijd laten zien',
                placeholder: '',
                required: false,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'datetime';
                },
              },
            },
            {
              key: 'readonlyFields',
              type: 'select',
              defaultValue: '',
              props: {
                label: 'Velden tonen in alleen-lezen modus',
                multiple: true,
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'readonly_entry';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'apiMappingId',
              type: 'select',
              defaultValue: '',
              props: {
                label: 'Selecteer API integratie',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'rdw' && selectedFieldType?.value !== 'kvk';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  return this.apiMappingsStore.apiMappings()[this.moduleId()]?.map(mapping => ({
                    value: mapping.id,
                    label: mapping.name,
                  }));
                },
              },
            },
            {
              key: 'vehicleFields',
              type: 'tags',
              defaultValue: [],
              props: {
                requireSelection: true,
                label: 'Voertuigvelden (read-only na opzoeken, bewerkbaar bij handmatige invoer)',
                placeholder: 'Zoek en kies RDW-velden...',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  return this.findSelectedFieldType(field)?.value !== 'rdw';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  // Show ALL fields the RDW API offers, not just the ones already mapped: match the
                  // selected mapping to its available API (by name, as the mappings admin does) and
                  // list its full outputFields. Value = apiField; label = catalog/humanized label.
                  const selectedMappingId = field.parent?.form?.get('apiMappingId')?.value;
                  const mappingsForModule = this.apiMappingsStore.apiMappings()[this.moduleId()];
                  const selectedMapping = mappingsForModule?.find(mapping => mapping.id === selectedMappingId);
                  const availableApi = this.apiMappingsStore
                    .availableApis()
                    .find(api => api.name === selectedMapping?.name);
                  return (availableApi?.outputFields ?? []).map(apiField => ({
                    value: apiField,
                    label: resolveRdwFieldMeta(apiField).label,
                  }));
                },
              },
            },
            {
              key: 'kvkStyle',
              type: 'select',
              defaultValue: 'standard',
              props: {
                label: 'Weergave KVK-veld',
                options: [
                  { value: 'standard', label: 'Standaard' },
                  { value: 'verify', label: 'Verificatie' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'kvk',
              },
            },
            {
              key: 'kvkLookupMode',
              type: 'select',
              defaultValue: 'number',
              props: {
                label: 'Bedrijf opzoeken via',
                description:
                  'Zoeken toont suggesties uit het KvK-register terwijl je typt; de gegevens worden opgehaald zodra je een bedrijf kiest.',
                options: [
                  { value: 'number', label: 'KVK-nummer' },
                  { value: 'search', label: 'Bedrijfsnaam of KVK-nummer (zoeken)' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  if (this.findSelectedFieldType(field)?.value !== 'kvk') {
                    return true;
                  }
                  // Verify style only — the standard KVK field stores the bare number as its value.
                  return field.parent?.form?.get('kvkStyle')?.value !== 'verify';
                },
              },
            },
            {
              key: 'kvkNotice',
              type: 'textarea',
              props: {
                label: 'Melding boven het KVK-veld',
                description:
                  'Toelichting die als informatieblok boven het veld verschijnt, bijvoorbeeld dat het nummer al is voorgevuld. Leeg = geen melding.',
                rows: 2,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  if (this.findSelectedFieldType(field)?.value !== 'kvk') {
                    return true;
                  }
                  // Verify style only — the standard KVK field has no notice slot.
                  return field.parent?.form?.get('kvkStyle')?.value !== 'verify';
                },
              },
            },
            {
              key: 'kvkHideCompanyName',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Bedrijfsnaam niet invulbaar',
                description:
                  'Laat het bedrijfsnaam-invoerveld weg bij handmatig invoeren. Voor een formulier dat al een eigen bedrijfsnaam-veld heeft, zodat de naam niet twee keer wordt ingevuld. De opgehaalde naam blijft wel op de resultaatkaart staan.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  if (this.findSelectedFieldType(field)?.value !== 'kvk') {
                    return true;
                  }
                  // Verify style only — the standard KVK field renders no sub-fields at all.
                  return field.parent?.form?.get('kvkStyle')?.value !== 'verify';
                },
              },
            },
            {
              key: 'kvkAddressTarget',
              type: 'input',
              props: {
                label: 'Adresveld vullen bij opzoeken',
                description:
                  'KeyPath van het adresveld dat de lookup vult, bijvoorbeeld general.address. Alleen ingevuld zolang dat adres nog leeg is, zodat een handmatige correctie blijft staan. Leeg = niets doorzetten.',
                placeholder: 'general.address',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  if (this.findSelectedFieldType(field)?.value !== 'kvk') {
                    return true;
                  }
                  // Verify style only — the standard KVK field has no resolved address of its own.
                  return field.parent?.form?.get('kvkStyle')?.value !== 'verify';
                },
              },
            },
            {
              key: 'kvkFields',
              type: 'tags',
              defaultValue: [],
              props: {
                requireSelection: true,
                label: 'Bedrijfsvelden (read-only na opzoeken, bewerkbaar bij handmatige invoer)',
                placeholder: 'Zoek en kies KVK-velden...',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  if (this.findSelectedFieldType(field)?.value !== 'kvk') {
                    return true;
                  }
                  // Verify style only — the standard KVK field has no configurable sub-fields.
                  return field.parent?.form?.get('kvkStyle')?.value !== 'verify';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  // Same catalog resolution as the RDW vehicleFields: match the selected mapping to
                  // its available API (by name) and list the full outputFields. Value = apiField.
                  const selectedMappingId = field.parent?.form?.get('apiMappingId')?.value;
                  const mappingsForModule = this.apiMappingsStore.apiMappings()[this.moduleId()];
                  const selectedMapping = mappingsForModule?.find(mapping => mapping.id === selectedMappingId);
                  const availableApi = this.apiMappingsStore
                    .availableApis()
                    .find(api => api.name === selectedMapping?.name);
                  return (availableApi?.outputFields ?? []).map(apiField => ({
                    value: apiField,
                    label: resolveKvkFieldMeta(apiField).label,
                  }));
                },
              },
            },
            {
              key: 'defaultValue',
              type: 'input',
              className: 'w-max-sm',
              defaultValue: '',
              props: {
                label: 'Standaard waarde',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    !selectedFieldType?.defaultValue ||
                    selectedFieldType?.options ||
                    selectedFieldType?.datatype[0] === 'boolean' ||
                    // Composite fields (field with subfields) use "Standaard waardes per subveld" instead.
                    !!selectedFieldType?.fieldGroup
                  );
                },
              },
            },
            {
              key: 'defaultValue',
              type: 'custom-select',
              className: 'w-max-sm',
              props: {
                label: 'Standaard waarde',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    (!selectedFieldType?.defaultValue || !selectedFieldType?.options) &&
                    selectedFieldType?.datatype[0] !== 'boolean'
                  );
                },
                'props.multiple': (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.datatype[0] === 'array';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType?.datatype?.[0] === 'boolean') {
                    return [
                      { value: true, label: 'Waar' },
                      { value: false, label: 'Onwaar' },
                    ];
                  }

                  const options = [];

                  const optionsArray = this.formService.findFieldValue(field, 'optionsArray') ?? [];

                  optionsArray?.forEach(optionsItem => {
                    if (optionsItem.type !== null && optionsItem.moduleCode !== null) {
                      const moduleId = this.modulesStore.moduleCodeToId(optionsItem.moduleCode);

                      this.formService
                        .generateOptions(
                          optionsItem ?? [],
                          this.generatedForms()[moduleId],
                          this.modulesStore.moduleCodeMapper(),
                          optionsArray.length > 1,
                          this.modulesStore.entries(),
                          this.modulesStore.taxonomies()
                        )
                        ?.forEach(option => {
                          options.push(option);
                        });

                      if (optionsItem.moduleCode === 'users') {
                        options.unshift({
                          value: 'currentUser',
                          label: 'Huidige gebruiker',
                        });
                      }

                      if (optionsItem.moduleCode === 'companies') {
                        options.unshift({
                          value: 'currentCompany',
                          label: 'Huidige bedrijf',
                        });
                      }

                      if (optionsItem.moduleCode === 'locations') {
                        options.unshift({
                          value: 'currentLocation',
                          label: 'Huidige vestiging',
                        });
                      }

                      options.unshift({
                        value: '',
                        label: 'Geen waarde',
                      });
                    }
                  });

                  return options;
                },
              },
            },
            {
              key: 'useIndexedDefaults',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Unieke standaard waarde per index',
                description: 'Schakel in om verschillende standaardwaarden per positie in een repeater in te stellen',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  // Only show for field types that support defaultValue
                  return !selectedFieldType?.defaultValue;
                },
              },
            },
            {
              key: 'defaultValuesByIndex',
              type: 'repeat',
              props: {
                label: 'Standaard waarden per index',
                addText: 'Index toevoegen',
                description:
                  'Items zonder geconfigureerde standaardwaarde krijgen een lege waarde. Index 0 = eerste item in repeater.',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'index',
                    type: 'custom-number',
                    className: 'w-1/4',
                    props: {
                      label: 'Index',
                      min: 0,
                      required: true,
                    },
                  },
                  // Input field for non-boolean, non-option fields
                  {
                    key: 'value',
                    type: 'input',
                    className: 'w-3/4',
                    props: {
                      label: 'Standaard waarde',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        const selectedFieldType = this.findSelectedFieldType(field);
                        return (
                          !selectedFieldType?.defaultValue ||
                          selectedFieldType?.options ||
                          selectedFieldType?.datatype[0] === 'boolean'
                        );
                      },
                    },
                  },
                  // Select field for boolean or option-based fields
                  {
                    key: 'value',
                    type: 'custom-select',
                    className: 'w-3/4',
                    props: {
                      label: 'Standaard waarde',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        const selectedFieldType = this.findSelectedFieldType(field);
                        return (
                          (!selectedFieldType?.defaultValue || !selectedFieldType?.options) &&
                          selectedFieldType?.datatype[0] !== 'boolean'
                        );
                      },
                      'props.multiple': (field: FormlyFieldConfig) => {
                        const selectedFieldType = this.findSelectedFieldType(field);
                        return selectedFieldType?.datatype[0] === 'array';
                      },
                      'props.options': (field: FormlyFieldConfig) => {
                        if (field.hide) {
                          return [];
                        }
                        const selectedFieldType = this.findSelectedFieldType(field);
                        if (selectedFieldType?.datatype?.[0] === 'boolean') {
                          return [
                            { value: true, label: 'Waar' },
                            { value: false, label: 'Onwaar' },
                          ];
                        }

                        const options = [];
                        const optionsArray = this.formService.findFieldValue(field, 'optionsArray') ?? [];

                        optionsArray?.forEach(optionsItem => {
                          if (optionsItem.type !== null && optionsItem.moduleCode !== null) {
                            const moduleId = this.modulesStore.moduleCodeToId(optionsItem.moduleCode);

                            this.formService
                              .generateOptions(
                                optionsItem ?? [],
                                this.generatedForms()[moduleId],
                                this.modulesStore.moduleCodeMapper(),
                                optionsArray.length > 1,
                                this.modulesStore.entries(),
                                this.modulesStore.taxonomies()
                              )
                              ?.forEach(option => {
                                options.push(option);
                              });

                            if (optionsItem.moduleCode === 'users') {
                              options.unshift({
                                value: 'currentUser',
                                label: 'Huidige gebruiker',
                              });
                            }

                            if (optionsItem.moduleCode === 'companies') {
                              options.unshift({
                                value: 'currentCompany',
                                label: 'Huidige bedrijf',
                              });
                            }

                            if (optionsItem.moduleCode === 'locations') {
                              options.unshift({
                                value: 'currentLocation',
                                label: 'Huidige vestiging',
                              });
                            }

                            options.unshift({
                              value: '',
                              label: 'Geen waarde',
                            });
                          }
                        });

                        return options;
                      },
                    },
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const useIndexedDefaults = this.formService.findFieldValue(field, 'useIndexedDefaults');
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !useIndexedDefaults || !selectedFieldType?.defaultValue;
                },
              },
            },
            {
              key: 'defaultValuesSubfields',
              type: 'repeat',
              props: {
                label: 'Standaard waardes per subveld',
                addText: 'Subveld toevoegen',
                description:
                  'Stel per subveld van dit samengestelde veld een standaardwaarde in (bijv. Geofencing-straal bij een adresveld).',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'subfieldKey',
                    type: 'custom-select',
                    className: 'w-1/2',
                    props: {
                      label: 'Subveld',
                      required: true,
                    },
                    expressions: {
                      'props.options': (field: FormlyFieldConfig) => {
                        const selectedFieldType = this.findSelectedFieldType(field);
                        return (selectedFieldType?.fieldGroup ?? [])
                          .filter(subField => subField.key)
                          .map(subField => ({
                            value: subField.key,
                            label: subField.props?.label ?? String(subField.key),
                          }));
                      },
                    },
                  },
                  {
                    key: 'value',
                    type: 'input',
                    className: 'w-1/2',
                    props: {
                      label: 'Standaard waarde',
                    },
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.fieldGroup || selectedFieldType?.configurableSubfields === false;
                },
              },
            },
            {
              key: 'hiddenSubfields',
              type: 'custom-select',
              className: 'w-max-sm',
              props: {
                label: 'Subvelden verbergen',
                multiple: true,
                description:
                  'Verborgen subvelden blijven onderdeel van het formulier en sturen hun (standaard)waarde mee. Combineer met een standaardwaarde als het subveld verplicht is.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.fieldGroup || selectedFieldType?.configurableSubfields === false;
                },
                'props.options': (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (selectedFieldType?.fieldGroup ?? [])
                    .filter(subField => subField.key)
                    .map(subField => ({
                      value: subField.key,
                      label: subField.props?.label ?? String(subField.key),
                    }));
                },
              },
            },
            {
              key: 'uploadType',
              type: 'custom-select',
              defaultValue: 'public-and-private',
              props: {
                label: 'Upload type',
                info: 'Selecteer of welke manier de gebruiker bestanden kan uploaden.',
                placeholder: '',
                options: [
                  {
                    label: 'Public en private',
                    value: 'public-and-private',
                  },
                  {
                    label: 'Public',
                    value: 'public',
                  },
                  {
                    label: 'Private',
                    value: 'private',
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'upload' && selectedFieldType?.value !== 'upload-document-entry';
                },
              },
            },
            {
              key: 'documentType',
              type: 'custom-select',
              defaultValue: '',
              props: {
                label: 'Document type',
                info: 'Wat voor type document is het.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'upload-document-entry' &&
                    selectedFieldType?.value !== 'signing-upload'
                  );
                },
                'props.options': (field: FormlyFieldConfig) => {
                  const documentModule = this.modulesStore.getModuleByCode('documents');
                  const taxonomy = this.modulesStore.taxonomies()[documentModule.id]?.find((taxonomy: Taxonomy) => {
                    return taxonomy.name === 'Documenttype';
                  });
                  return (
                    taxonomy?.entries.map(entry => {
                      return {
                        value: entry,
                        label: entry.selectedInput,
                      };
                    }) ?? []
                  );
                },
              },
            },
            {
              key: 'acceptedTypes',
              type: 'custom-select',
              defaultValue: '',
              // Authored on upload-document-entry, but `upload` and `required-documents-checklist`
              // read the same prop at runtime. Without this opt-out Formly's `resetOnHide: true`
              // drops it on save, which silently widens the accepted file types instead of
              // narrowing them.
              resetOnHide: false,
              props: {
                multiple: true,
                label: 'Wat voor type bestanden mogen worden geüpload?',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  // All four upload types read this prop at runtime, so all four must be able to
                  // set it (#20817) — it used to be visible for upload-document-entry only.
                  return !(UPLOAD_FIELD_TYPES as readonly string[]).includes(selectedFieldType?.value ?? '');
                },
                'props.options': (field: FormlyFieldConfig) => {
                  // list of accepted file types for example: '.pdf, .docx, .xlsx'
                  return [
                    { value: '.pdf', label: 'PDF (.pdf)' },
                    { value: '.docx', label: 'Word (.docx)' },
                    { value: '.xlsx', label: 'Excel (.xlsx)' },
                    { value: '.jpg', label: 'JPG (.jpg)' },
                    { value: '.jpeg', label: 'JPEG (.jpeg)' },
                    { value: '.png', label: 'PNG (.png)' },
                    { value: '.webp', label: 'WebP (.webp)' },
                    { value: '.txt', label: 'Tekst (.txt)' },
                    { value: '.csv', label: 'CSV (.csv)' },
                    { value: '.svg', label: 'SVG (.svg)' },
                  ];
                },
              },
            },
            {
              key: 'maxFileSizeMb',
              type: 'input',
              defaultValue: null,
              // Same exposure as `acceptedTypes`: read by `upload` and
              // `required-documents-checklist`, so losing it on save quietly restores the 50 MB
              // default over a configured limit.
              resetOnHide: false,
              props: {
                type: 'number',
                label: 'Maximale bestandsgrootte (MB)',
                description: 'Laat leeg voor de standaard van 50 MB. De dropzone toont de limiet.',
                min: 1,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  // All four upload types read this prop at runtime, so all four must be able to
                  // set it (#20817) — it used to be visible for upload-document-entry only.
                  return !(UPLOAD_FIELD_TYPES as readonly string[]).includes(selectedFieldType?.value ?? '');
                },
              },
            },
            {
              key: 'sourceFieldPath',
              type: 'input',
              defaultValue: '',
              props: {
                label: 'Bronveld pad',
                description:
                  'Het pad naar het veld waar het document staat (bijv. "entry.contract" of "contract"). Laat leeg om de eigen waarde van dit veld te gebruiken.',
                placeholder: 'entry.contract',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'download-document' && selectedFieldType?.value !== 'signing-sign'
                  );
                },
              },
            },
            {
              key: 'submitterFieldPath',
              type: 'select',
              defaultValue: '',
              props: {
                label: 'Ondertekenaar invoerveld',
                description:
                  'Het invoerveld in dit formulier dat naar een gebruiker verwijst. De geselecteerde gebruiker wordt de ondertekenaar.',
                placeholder: '',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'signing-upload';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }
                  const fields = FormHelper.getFields(this.generatedForms()[this.module().id]);
                  return fields
                    .filter(formField =>
                      formField.props?.optionsArray?.some(
                        optionsItem => optionsItem?.moduleCode === 'users' && optionsItem?.optionsType === 'module'
                      )
                    )
                    .map(formField => ({
                      value: formField.props.keyPath,
                      label: formField.props.label,
                    }));
                },
              },
            },
            {
              key: 'alert.type',
              type: 'segment-buttons',
              className: 'w-full mb-4',
              defaultValue: 'info',
              props: {
                label: 'Type alert',
                description: 'Selecteer het type alert',
                options: [
                  { value: 'primary', label: 'Primary' },
                  { value: 'accent', label: 'Accent' },
                  { value: 'warn', label: 'Warn' },
                  { value: 'basic', label: 'Basic' },
                  { value: 'info', label: 'Info' },
                  { value: 'success', label: 'Success' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'error', label: 'Error' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'fuse-alert';
                },
              },
            },
            {
              key: 'alert.appearance',
              type: 'segment-buttons',
              className: 'w-full mb-4',
              defaultValue: 'soft',
              props: {
                label: 'Appearance',
                description: 'Selecteer de stijl van de alert',
                options: [
                  { value: 'soft', label: 'Soft' },
                  { value: 'outline', label: 'Outline' },
                  { value: 'fill', label: 'Fill' },
                  { value: 'border', label: 'Border' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'fuse-alert';
                },
              },
            },
            {
              key: 'alert.content',
              type: 'textarea',
              props: {
                label: 'Alert inhoud',
                description: 'De tekst die in de alert wordt getoond',
                placeholder: 'Voer de inhoud voor de alert in',
                rows: 3,
                required: false,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'fuse-alert';
                },
              },
            },
            {
              key: 'alert.showIcon',
              type: 'toggle',
              defaultValue: true,
              className: 'inline-block mt-4',
              props: {
                label: 'Toon icoon',
                description: 'Toon of verberg het alert icoon',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'fuse-alert';
                },
              },
            },
            {
              key: 'alert.dismissible',
              type: 'toggle',
              defaultValue: false,
              className: 'inline-block mt-4',
              props: {
                label: 'Sluitbaar',
                description: 'Maakt het mogelijk om de alert te sluiten',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'fuse-alert';
                },
              },
            },
            {
              key: 'alert.dismissed',
              type: 'toggle',
              defaultValue: false,
              className: 'inline-block mt-4',
              props: {
                label: 'Standaard gesloten',
                description: 'Alert is standaard gesloten (alleen relevant als sluitbaar is aangevinkt)',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  const dismissible = field.parent.form?.get('dismissible')?.value;
                  return selectedFieldType?.value !== 'fuse-alert' || !dismissible;
                },
              },
            },
            {
              key: 'requireSelection',
              type: 'toggle',
              defaultValue: false,
              className: 'w-max-sm',
              props: {
                label: 'Optie selecteren verplicht',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  const isAutocompleteType =
                    selectedFieldType?.value === 'autocomplete' ||
                    selectedFieldType?.value === 'tags' ||
                    selectedFieldType?.value === 'dynamic-module-autocomplete';
                  return !isAutocompleteType;
                },
              },
            },
            {
              // Weergave van de vereiste-documenten-checklist. Kaarten blijft de default, zodat
              // bestaande formulieren ongewijzigd renderen (Figma 3802:165013 voor de regels).
              key: 'documentsDisplayMode',
              type: 'select',
              className: 'w-max-sm',
              props: {
                label: 'Weergave',
                description:
                  'Kaarten: elk vereist document krijgt een eigen kaart met statusbadge, bestandsblok ' +
                  'en een "…"-menu. Regels: compacte lijst met een scheidingslijn per regel en één knop ' +
                  'rechts — de weergave uit het onboarding-ontwerp. Let op: in de regelweergave is er ' +
                  'géén "…"-menu, dus bekijken, downloaden, archiveren en "Voorbeeld downloaden" ' +
                  'zijn daar niet bereikbaar.',
                placeholder: '',
                options: [
                  { value: 'cards', label: 'Kaarten' },
                  { value: 'rows', label: 'Regels' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) =>
                  this.findSelectedFieldType(field)?.value !== 'required-documents-checklist',
              },
            },
            {
              // Review flagging — only on the Onboarding overview. The flags are stored in this
              // field's own value, so the module no longer needs a separate flags field for
              // it.
              key: 'reviewFlagging',
              type: 'toggle',
              defaultValue: true,
              className: 'w-max-sm',
              props: {
                label: 'Markeren toestaan',
                description:
                  'Aan: de beoordelaar kan secties en documenten markeren, met een reden die in de ' +
                  'afkeurtekst terechtkomt. Uit: het overzicht is alleen-lezen — geen markeerknoppen ' +
                  'en er wordt niets weggeschreven.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'readonly_onboarding',
              },
            },
            this.#reviewDocumentActionField(
              'reviewApproveActionId',
              'Documentactie bij goedkeuren',
              'De exacte interface-actie op Documentrecords die openstaande documenten goedkeurt.'
            ),
            this.#reviewDocumentActionField(
              'reviewRejectActionId',
              'Documentactie bij afkeuren',
              'De exacte interface-actie op Documentrecords die gemarkeerde documenten afwijst.'
            ),
            {
              key: 'tagsDisplayMode',
              type: 'select',
              className: 'w-max-sm',
              props: {
                label: 'Weergave',
                description:
                  'Standaard: toevoegknop met overlay zodra het veld opties heeft (Figma). ' +
                  'Inline: altijd zichtbaar invoerveld met autocomplete (en de enige weergave zonder opties). ' +
                  'Meerdere knoppen: alle opties staan meteen zichtbaar naast elkaar — kies dit alleen bij een korte lijst met korte labels.',
                placeholder: '',
                options: [
                  { value: 'inline', label: 'Inline invoerveld' },
                  { value: 'menu', label: 'Eén knop met keuzelijst' },
                  { value: 'buttons', label: 'Meerdere knoppen' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
              },
            },
            {
              key: 'tagsMenuMultiSelect',
              type: 'toggle',
              defaultValue: false,
              className: 'w-max-sm',
              props: {
                label: 'Meerdere tegelijk kiezen (overlay blijft open)',
                description:
                  'De toevoeg-overlay blijft open en toont gekozen opties met een vinkje; ' +
                  'nogmaals klikken haalt ze weg. Alleen van toepassing op de toevoegknop-weergave.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
              },
            },
            {
              key: 'maxTags',
              type: 'input',
              className: 'w-max-sm',
              props: {
                label: 'Maximaal aantal tags',
                description:
                  'Alleen voor de knoppen-weergave. Leeg = geen limiet. Bij het maximum kan de gebruiker ' +
                  'alleen nog tags weghalen; geblokkeerde knoppen krijgen een tooltip met de reden.',
                placeholder: '',
                type: 'number',
                min: 1,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) =>
                  this.findSelectedFieldType(field)?.value !== 'tags' ||
                  this.formService.findFieldValue(field, 'tagsDisplayMode') !== 'buttons',
              },
            },
            {
              key: 'tagsNestByGroup',
              type: 'toggle',
              defaultValue: false,
              className: 'w-max-sm',
              props: {
                label: 'Groeperen als keuze in twee stappen',
                description:
                  'Gebruikt het groepeerveld van de gekoppelde module als eerste stap: de overlay toont ' +
                  'eerst de groepen (bijvoorbeeld landen) en pas daarna de opties erbinnen (de regio’s), ' +
                  'met een "Alles in ..."-keuze. Zonder deze optie blijft het één platte lijst.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
              },
            },
            {
              key: 'nestedChildrenLabel',
              type: 'input',
              className: 'w-max-sm',
              props: {
                label: 'Naam van het tweede niveau',
                description:
                  'Staat achter de groepsnaam in het kruimelpad van de tweede stap, bijvoorbeeld ' +
                  '"Duitsland > Regio’s". Leeg laten toont "Opties".',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
              },
            },
            {
              key: 'tagsShowLabel',
              type: 'toggle',
              defaultValue: false,
              className: 'w-max-sm',
              props: {
                label: 'Label boven de knop tonen',
                description:
                  'Standaard staat er geen label boven een tags-veld, want de knop noemt het onderwerp ' +
                  'al ("Functies toevoegen"). Zet dit aan als het label iets anders zegt dan de knop — ' +
                  'vul dan ook "Tekst toevoegknop" in, anders staat er twee keer hetzelfde.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
              },
            },
            {
              key: 'showCreateButton',
              type: 'toggle',
              defaultValue: false,
              className: 'w-max-sm',
              props: {
                label: 'Mag nieuwe items aanmaken voor geselecteerde module',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  const optionsArray = this.formService.findFieldValue(field, 'optionsArray');

                  const hasModule = optionsArray?.find(optionsItem => {
                    return optionsItem?.optionsType === 'module';
                  });

                  return (
                    !selectedFieldType?.options ||
                    (selectedFieldType?.value !== 'autocomplete' && selectedFieldType?.value !== 'tags') ||
                    !hasModule
                  );
                },
              },
            },
            {
              key: 'createFormId',
              type: 'custom-select',
              defaultValue: '',
              className: 'w-max-sm',
              props: {
                label: 'Aanmaakformulier',
                description:
                  'Welk aanmaakformulier de "+"-knop opent. "Standaard" gebruikt het formulier van de gekoppelde module.',
                placeholder: '',
                options: [],
              },
              expressions: {
                // Reuse the showCreateButton hide rule: only relevant for autocomplete fields
                // that may create new items (matches defaultValuesAutocomplete hide ~line 3514).
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  const showCreateButton = this.formService.findFieldValue(field, 'showCreateButton');
                  return selectedFieldType?.value !== 'autocomplete' || !showCreateButton;
                },
                'props.options': () => {
                  return [
                    { label: 'Standaard', value: '' },
                    ...this.modulesStore.enabledForms().map(form => ({ label: form.title, value: form.id })),
                  ];
                },
              },
            },
            {
              key: 'addressStyle',
              type: 'select',
              defaultValue: 'standard',
              props: {
                label: 'Weergave adresveld',
                description:
                  'Eén regel toont het volledige adres in één invoerveld met het veldlabel erboven; de losse adresvelden verschijnen pas na "Adres handmatig invoeren".',
                options: [
                  { value: 'standard', label: 'Standaard' },
                  { value: 'single', label: 'Eén regel' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'address';
                },
              },
            },
            {
              key: 'showMap',
              type: 'toggle',
              defaultValue: true,
              props: {
                label: 'Toon kaart en geolocatie',
                description: 'Hiermee kan de gebruiker de locatie op een kaart zien en selecteren',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'address';
                },
              },
            },
            {
              key: 'showFullAddress',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Toon volledig adres altijd',
                description: 'Toon altijd alle adresvelden, ook wanneer er nog geen adres is geselecteerd',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'address';
                },
              },
            },
            {
              key: 'geoRadiusLabel',
              type: 'input',
              props: {
                label: 'Geo-radius label',
                description: 'Het label van het radius veld',
                placeholder: 'Geofencing-straal',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'address';
                },
              },
            },
            {
              key: 'geoRadiusUnit',
              type: 'select',
              defaultValue: 'meter',
              props: {
                label: 'Geo-radius eenheid',
                description: 'De eenheid voor het radius veld',
                options: [
                  { label: 'Meter', value: 'meter' },
                  { label: 'Kilometer', value: 'km' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'address';
                },
              },
            },
            {
              key: 'showBagVerify',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Toon BAG verificatie knop',
                description:
                  'Voeg een knop toe aan het adresveld om het adres te verifiëren via een BAG API-integratie.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'address';
                },
              },
            },
            {
              key: 'bagApiMappingId',
              type: 'select',
              defaultValue: '',
              props: {
                label: 'BAG API integratie',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType?.value !== 'address') return true;
                  return this.formService.findFieldValue(field, 'showBagVerify') !== true;
                },
                'props.options': field => {
                  if (field.hide) return [];
                  return this.apiMappingsStore.apiMappings()[this.moduleId()]?.map(mapping => ({
                    value: mapping.id,
                    label: mapping.name,
                  }));
                },
              },
            },
            {
              key: 'bagVerifyShowUserRole',
              type: 'custom-multicheckbox',
              props: {
                label: 'BAG verificatie knop tonen voor rol(len)',
                description: 'Laat leeg om de knop voor iedereen zichtbaar te maken.',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType?.value !== 'address') return true;
                  return this.formService.findFieldValue(field, 'showBagVerify') !== true;
                },
                'props.options': () => {
                  return this.userRolesStore.userRoles().map(role => ({
                    value: role.id,
                    label: role.name,
                  }));
                },
              },
            },
            {
              key: 'hideIfOptionsEmpty',
              type: 'toggle',
              defaultValue: false,
              // The block only shows for tags/autocomplete with a module-backed options array, but
              // custom-select, custom-radio, custom-multicheckbox and custom-simple-multicheckbox
              // all read the prop. It also hides when the options array stops pointing at a module,
              // so without this opt-out the toggle is lost on an unrelated edit.
              resetOnHide: false,
              className: 'w-max-sm',
              props: {
                label: 'Verberg dit veld als er geen opties zijn.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  const optionsArray = this.formService.findFieldValue(field, 'optionsArray');

                  const hasModule = optionsArray?.find(optionsItem => {
                    return optionsItem?.optionsType === 'module';
                  });

                  return (
                    (selectedFieldType?.value !== 'tags' && selectedFieldType?.value !== 'autocomplete') || !hasModule
                  );
                },
              },
            },
            {
              key: 'valueShape',
              type: 'select',
              defaultValue: 'objectMap',
              className: 'w-max-sm',
              props: {
                label: 'Opslag-type waarde',
                description:
                  'Object-map (standaard) slaat op als {key: bool} — vereist voor de parent-pref-filter ' +
                  '(compareSubField + istrue). Kies "Lijst" alleen voor speciale integraties die string[] verwachten.',
                placeholder: 'Selecteer opslag-type',
                options: [
                  { value: 'objectMap', label: 'Object-map (key: bool) — standaard, voor parent-pref-filter' },
                  { value: 'array', label: 'Lijst (array) — string[]' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'custom-multicheckbox';
                },
              },
            },
            {
              // Radio (custom-radio) only: orientation of the option cards.
              key: 'layout',
              type: 'segment-buttons',
              defaultValue: 'vertical',
              className: 'inline-block mt-2 w-full',
              props: {
                label: 'Uitlijning',
                options: [
                  { value: 'vertical', label: 'Onder elkaar' },
                  { value: 'horizontal', label: 'Naast elkaar' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'custom-radio';
                },
              },
            },

            // linked/required/inline-entries items fields
            {
              key: 'moduleCode',
              type: 'select',
              props: {
                label: 'Module',
                placeholder: 'Selecteer module',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'linked-items' &&
                    selectedFieldType?.value !== 'linkage-items' &&
                    selectedFieldType?.value !== 'linkage-tags' &&
                    selectedFieldType?.value !== 'linkage-select' &&
                    selectedFieldType?.value !== 'select-linked-entry' &&
                    selectedFieldType?.value !== 'inline-entries' &&
                    selectedFieldType?.value !== 'required-items' &&
                    selectedFieldType?.value !== 'readonly_entry' &&
                    selectedFieldType?.value !== 'required-documents-checklist'
                  );
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  return this.modulesStore.modules().map(module => ({
                    value: module.code,
                    label: module.title,
                  }));
                },
              },
            },

            {
              key: 'cronRequired',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Herhaling verplicht',
                description: 'Hierdoor wordt de mogelijkeheid om een item aan te maken zonder herhaling uitgeschakeld.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'cron';
                },
              },
            },
            {
              key: 'dateRange',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Datum range',
                description: "Gebruik dit veld om een datum range in te stellen. Bijvoorbeeld 'van' en 'tot'.",
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'cron';
                },
              },
            },
            {
              key: 'fullDay',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Hele dag(en)',
                description: 'Schakel de van/tot tijden uit en gebruik volledige dagen.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'cron';
                },
              },
            },
            {
              key: 'endTimeOptional',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Eindtijd optioneel',
                description:
                  'Maak het "Tot" tijdveld niet verplicht. Zonder eindtijd wordt een duur van 24 uur aangenomen.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'cron';
                },
              },
            },
            {
              key: 'cardImage',
              type: 'select',
              props: {
                label: 'Selecteer veld voor image',
                multiple: false,
                placeholder: 'Selecteer velden',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'select-linked-entry';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'cardTitle',
              type: 'select',
              props: {
                label: 'Selecteer veld voor titel',
                multiple: false,
                placeholder: 'Selecteer velden',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'select-linked-entry';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'cardSubTitle',
              type: 'select',
              props: {
                label: 'Selecteer veld voor subTitel',
                multiple: false,
                placeholder: 'Selecteer velden',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'select-linked-entry';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'cardOtherInformation',
              type: 'select',
              props: {
                label: 'Selecteer veld voor extra informatie',
                multiple: true,
                placeholder: 'Selecteer velden',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'select-linked-entry';
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'moduleLinkedField',
              type: 'select',
              props: {
                label: 'Selecteer invoerveld die verwijst naar huidige module',
                description: '',
                placeholder: '',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);

                  return (
                    selectedFieldType?.value !== 'linked-items' &&
                    selectedFieldType?.value !== 'select-linked-entry' &&
                    selectedFieldType?.value !== 'required-items' &&
                    selectedFieldType?.value !== 'required-documents-checklist'
                  );
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }

                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const fields = FormHelper.getFields(this.generatedForms()[selectedModule.id]);

                  return fields
                    .filter(field => {
                      if (field.key === 'entryId' || field.key === 'my-order-id') {
                        return true;
                      }

                      return field.props?.optionsArray?.find(optionsItem => {
                        if (optionsItem === null) {
                          return false;
                        }
                        return optionsItem.moduleCode === this.module().code && optionsItem.optionsType === 'module';
                      });
                    })
                    .map(field => {
                      return {
                        value: field.props.keyPath,
                        label: field.props.label,
                      };
                    });
                },
              },
            },
            {
              key: 'linkedFieldA',
              type: 'select',
              props: {
                label: 'Gelinkt veld A (item-1)',
                description: 'Selecteer het eerste entry-veld in de koppelingen-module',
                placeholder: '',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'linkage-items' &&
                    selectedFieldType?.value !== 'linkage-tags' &&
                    selectedFieldType?.value !== 'linkage-select'
                  );
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }

                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'linkedFieldB',
              type: 'select',
              props: {
                label: 'Gelinkt veld B (item-2)',
                description: 'Selecteer het tweede entry-veld in de koppelingen-module',
                placeholder: '',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'linkage-items' &&
                    selectedFieldType?.value !== 'linkage-tags' &&
                    selectedFieldType?.value !== 'linkage-select'
                  );
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }

                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'searchModuleCode',
              type: 'select',
              props: {
                label: 'Zoekmodule',
                description: 'De module waarin gezocht wordt naar items om te koppelen',
                placeholder: 'Selecteer module',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'linkage-tags' &&
                    selectedFieldType?.value !== 'linkage-items' &&
                    selectedFieldType?.value !== 'linkage-select'
                  );
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  return this.modulesStore.modules().map(module => ({
                    value: module.code,
                    label: module.title,
                  }));
                },
              },
            },
            {
              key: 'maxSelections',
              type: 'select',
              props: {
                label: 'Selecteer invoerveld die verwijst naar hoeveel items geselecteerd mogen worden',
                description: '',
                placeholder: '',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);

                  return selectedFieldType?.value !== 'select-linked-entry';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }

                  const fields = FormHelper.getFields(this.generatedForms()[this.module().id]);

                  return fields
                    .filter(field => {
                      return field.props?.datatype?.includes('number');
                    })
                    .map(field => {
                      return {
                        value: field.props.keyPath,
                        label: field.props.label,
                      };
                    });
                },
              },
            },
            {
              key: 'linkedRepeaterField',
              type: 'select',
              props: {
                label: 'Selecteer repeater invoerveld die verwijst de eisen',
                description: '',
                placeholder: '',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);

                  return selectedFieldType?.value !== 'required-items';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }
                  const fields = FormHelper.getFields(this.generatedForms()[this.moduleId()]);

                  return fields
                    .filter(field => {
                      return field.type === 'repeat';
                    })
                    .map(field => {
                      return {
                        value: field.props.keyPath,
                        label: field.props.label,
                      };
                    });
                },
              },
            },
            {
              key: 'optionsArray',
              type: 'repeat_reorder',
              props: {
                initialCount: 1,
                label: 'Opties voor invoerveld',
                addText: 'Meer opties toevoegen',
              },
              fieldArray: {
                className: 'mt-4 w-full',
                fieldGroup: [
                  {
                    props: { label: 'Opties' },
                    fieldGroup: [
                      {
                        key: 'optionsType',
                        type: 'segment-buttons',
                        className: 'inline-block mt-2 w-full',
                        defaultValue: 'manual',
                        props: {
                          options: [
                            { value: 'manual', label: 'Handmatig' },
                            { value: 'taxonomy', label: 'Eigenschap' },
                            { value: 'module', label: 'Module' },
                          ],
                        },
                        hooks: {
                          onInit: (field: FormlyFieldConfig) => {
                            if (this.syncManualModuleCode(field)) {
                              this.queueDriftReconcile();
                            }
                            if (!field.formControl || this.#moduleCodeSyncedControls.has(field.formControl)) return;
                            this.#moduleCodeSyncedControls.add(field.formControl);
                            field.formControl.valueChanges
                              .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
                              .subscribe(() => this.syncManualModuleCode(field));
                          },
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => !this.findSelectedFieldType(field)?.options,
                        },
                      },
                      {
                        // manual options
                        key: 'options',
                        type: 'repeat',
                        props: {
                          label: '',
                          addText: 'Optie toevoegen',
                        },
                        fieldArray: {
                          className: 'mt-4 w-full',
                          fieldGroup: [
                            {
                              fieldGroup: [
                                {
                                  key: 'option',
                                  type: 'input',
                                },
                                {
                                  // Legacy/manual options may use a display label separate from the stored value.
                                  // Keep it in the Formly model so an unrelated builder save cannot remove it.
                                  key: 'label',
                                  type: 'input',
                                  hide: true,
                                  resetOnHide: false,
                                },
                                {
                                  key: 'value',
                                  type: 'input',
                                  hide: true,
                                  resetOnHide: false,
                                },
                                {
                                  // Radio-only: helper text shown under the option card.
                                  key: 'description',
                                  type: 'input',
                                  props: {
                                    label: 'Omschrijving',
                                    description: 'Wordt getoond onder de optie',
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) =>
                                      this.findSelectedFieldType(field)?.value !== 'custom-radio',
                                  },
                                },
                                {
                                  // Radio-only: shows an "Aanbevolen" badge on the option card.
                                  key: 'recommended',
                                  type: 'toggle',
                                  defaultValue: false,
                                  props: {
                                    label: 'Aanbevolen',
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) =>
                                      this.findSelectedFieldType(field)?.value !== 'custom-radio',
                                  },
                                },
                                {
                                  // Chip color for table display (same palette as taxonomy options).
                                  key: 'colorRole',
                                  type: 'custom-select',
                                  props: {
                                    label: 'Kleur',
                                    description: 'Kleur van het label in de tabelweergave',
                                    options: [
                                      { value: '', label: 'Geen' },
                                      ...TAXONOMY_COLOR_ROLES.map(role => ({ value: role.key, label: role.label })),
                                      { value: 'custom', label: 'Eigen kleur' },
                                    ],
                                  },
                                },
                                {
                                  key: 'colorHex',
                                  type: 'colorpicker',
                                  props: {
                                    label: 'Eigen kleur',
                                  },
                                  expressions: {
                                    // Only show the hex picker when "Custom" is chosen.
                                    hide: (field: FormlyFieldConfig) => field.model?.['colorRole'] !== 'custom',
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            if (selectedFieldType?.options) {
                              const optionsTypeValue = this.formService.findFieldValue(field, 'optionsType');
                              return optionsTypeValue !== 'manual';
                            }
                            return true;
                          },
                        },
                      },
                      {
                        key: 'moduleCode',
                        type: 'input',
                        defaultValue: this.module().code,
                        className: 'hidden',
                        props: {
                          label: 'Module naam',
                          placeholder: '',
                        },
                      },
                      {
                        // Toggle to surface the "other module" picker. When OFF the
                        // taxonomy is implicitly read from the current module via the
                        // hidden moduleCode input above — admins don't need to pick
                        // anything in the common case. Initialised true for existing
                        // fields that already reference a different module so admins
                        // can see + edit the saved cross-module ref.
                        key: 'useExternalModuleTaxonomy',
                        type: 'custom-toggle',
                        // CustomToggleComponent persists as string 'true'/'false'
                        // (toggle.component.ts:55). Match that convention so the
                        // hide-expression on the moduleCode select can do a strict
                        // equality check instead of relying on JS-falsy semantics
                        // (where `!'false'` would be `false` and surface the dropdown).
                        defaultValue: 'false',
                        className: 'mt-2',
                        props: {
                          label: 'Taxonomy uit andere module ophalen',
                          description:
                            'Standaard wordt de taxonomy van de huidige module gebruikt. Schakel in om een taxonomy uit een andere module te selecteren.',
                        },
                        hooks: {
                          onInit: (field: FormlyFieldConfig) => {
                            const currentValue = field.formControl?.value;
                            if (
                              currentValue === 'true' ||
                              currentValue === 'false' ||
                              currentValue === true ||
                              currentValue === false
                            ) {
                              return;
                            }
                            const moduleCode = this.formService.findFieldValue(field, 'moduleCode');
                            const isExternal = !!moduleCode && moduleCode !== this.module().code;
                            field.formControl?.setValue(isExternal ? 'true' : 'false', { emitEvent: false });
                          },
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            return selectedFieldType?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'taxonomy'
                              : true;
                          },
                        },
                      },
                      {
                        // Source-module selector for taxonomy mode. Defaults to the
                        // current module via the hidden defaultValue input above.
                        // Visible only when the admin explicitly opted in via the
                        // `useExternalModuleTaxonomy` toggle — keeps the common-case
                        // UI uncluttered (no choice required for same-module taxonomies).
                        key: 'moduleCode',
                        type: 'select',
                        props: {
                          label: 'Module van eigenschap',
                          placeholder: 'Selecteer module',
                          description: 'Welke module bezit de eigenschap die je wilt gebruiken?',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            if (!selectedFieldType?.options) return true;
                            if (this.formService.findFieldValue(field, 'optionsType') !== 'taxonomy') return true;
                            // CustomToggleComponent stores its value as string ('true' | 'false'),
                            // so strict equality is required — relying on JS-truthiness here
                            // would treat the string 'false' as truthy and surface the dropdown.
                            const toggle = this.formService.findFieldValue(field, 'useExternalModuleTaxonomy');
                            return toggle !== true && toggle !== 'true';
                          },
                          'props.options': field => {
                            if (field.hide) {
                              return [];
                            }
                            return this.modulesStore.modules().map(module => ({
                              value: module.code,
                              label: module.title,
                            }));
                          },
                        },
                      },
                      {
                        key: 'taxonomyId',
                        type: 'select',
                        props: {
                          label: 'Eigenschap',
                          placeholder: 'Selecteer eigenschap',
                          options: [],
                        },
                        expressions: {
                          hide: field =>
                            this.findSelectedFieldType(field)?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'taxonomy'
                              : true,
                          'props.options': field => {
                            if (field.hide) {
                              return [];
                            }
                            // Read taxonomies from the selected source module (default:
                            // current module). Empty list when the picked module has
                            // no taxonomies yet.
                            const selectedModuleCode =
                              this.formService.findFieldValue(field, 'moduleCode') ?? this.module().code;
                            const selectedModule = this.modulesStore
                              .modules()
                              .find(module => module.code === selectedModuleCode);
                            const sourceModuleId = selectedModule?.id ?? this.module().id;
                            return this.modulesStore.taxonomies()[sourceModuleId]?.map(taxonomy => ({
                              value: taxonomy.id,
                              label: taxonomy.name,
                            }));
                          },
                        },
                      },
                      {
                        // Narrow the taxonomy options for THIS field only: show a
                        // selection or hide a selection. Lets two fields on the same
                        // eigenschap offer different subsets (per create-form branch).
                        key: 'optionFilterMode',
                        type: 'custom-select',
                        defaultValue: TaxonomyOptionFilterModeEnum.All,
                        props: {
                          label: 'Opties beperken',
                          description: 'Bepaal welke opties van deze eigenschap in dit veld beschikbaar zijn',
                          options: [
                            { value: TaxonomyOptionFilterModeEnum.All, label: 'Alle opties' },
                            { value: TaxonomyOptionFilterModeEnum.Include, label: 'Alleen geselecteerde opties' },
                            {
                              value: TaxonomyOptionFilterModeEnum.Exclude,
                              label: 'Alles behalve geselecteerde opties',
                            },
                          ],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) =>
                            this.findSelectedFieldType(field)?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'taxonomy' ||
                                !this.formService.findFieldValue(field, 'taxonomyId')
                              : true,
                        },
                      },
                      {
                        // Option slugs the narrowing applies to. Slugs (not ids) keep the
                        // config valid after an export/import to another environment.
                        key: 'optionFilterSlugs',
                        type: 'custom-select',
                        props: {
                          multiple: true,
                          label: 'Opties',
                          placeholder: 'Selecteer opties',
                          description: 'Zonder selectie blijven alle opties beschikbaar',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            if (!this.findSelectedFieldType(field)?.options) return true;
                            if (this.formService.findFieldValue(field, 'optionsType') !== 'taxonomy') return true;
                            const mode = field.model?.['optionFilterMode'];
                            return (
                              mode !== TaxonomyOptionFilterModeEnum.Include &&
                              mode !== TaxonomyOptionFilterModeEnum.Exclude
                            );
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            return this.taxonomyEntryOptions(field);
                          },
                        },
                      },
                      {
                        key: 'moduleCode',
                        type: 'select',
                        props: {
                          label: 'Module',
                          placeholder: 'Selecteer module',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);

                            return selectedFieldType?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'module'
                              : true;
                          },
                          'props.options': field => {
                            if (field.hide) {
                              return [];
                            }
                            return this.modulesStore.modules().map(module => ({
                              value: module.code,
                              label: module.title,
                            }));
                          },
                        },
                      },
                      {
                        key: 'moduleLinkedField',
                        type: 'select',
                        props: {
                          label: 'Selecteer invoerveld',
                          description: 'Deze waarde wordt gebruikt als label bij de opties',
                          placeholder: '',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            return selectedFieldType?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'module'
                              : true;
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            const selectedModule = this.modulesStore
                              .modules()
                              .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                            if (selectedModule === undefined) {
                              return;
                            }

                            return this.formService.getFieldsOptionsOfModule(this.generatedForms()[selectedModule.id]);
                          },
                        },
                      },
                      {
                        key: 'moduleValueType',
                        type: 'select',
                        defaultValue: 'entry',
                        props: {
                          label: 'Waarde overnemen van:',
                          description: 'Deze waarde wordt gebruikt als value bij de opties',
                          placeholder: '',
                          options: [
                            {
                              value: 'entry',
                              label: 'Hele entry',
                            },
                            {
                              value: 'entry-small',
                              label: 'Beperkte entry',
                            },
                            {
                              value: 'id',
                              label: 'Alleen id',
                            },
                            {
                              value: 'field',
                              label: 'Waarde invoerveld',
                            },
                          ],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            return selectedFieldType?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'module'
                              : true;
                          },
                        },
                      },
                      {
                        key: 'syncEntry',
                        type: 'toggle',
                        defaultValue: false,
                        props: {
                          label: 'Synchroniseer module data',
                          description:
                            'Synchroniseer de laatste versie van de geselecteerde entry als de hoofdentry veranderd. ',
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);

                            return selectedFieldType?.options
                              ? this.formService.findFieldValue(field, 'optionsType') !== 'module'
                              : true;
                          },
                        },
                      },
                      {
                        key: 'groupByField',
                        type: 'select',
                        props: {
                          label: 'Groepeer op veld',
                          description: 'Selecteer een veld om opties te groeperen',
                          placeholder: 'Geen groepering',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            return selectedFieldType?.value !== 'autocomplete'
                              ? true
                              : this.formService.findFieldValue(field, 'optionsType') !== 'module';
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            const selectedModule = this.modulesStore
                              .modules()
                              .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                            if (selectedModule === undefined) {
                              return [];
                            }

                            return [
                              { value: null, label: 'Geen groepering' },
                              ...this.formService.getFieldsOptionsOfModule(this.generatedForms()[selectedModule.id]),
                            ];
                          },
                        },
                      },
                      {
                        key: 'additionalInfoTemplate',
                        type: 'textarea',
                        props: {
                          label: 'Extra informatie template',
                          description:
                            'Ondersteunde syntax:\n' +
                            '• Veld: {{entry.fieldName}}\n' +
                            '• Bracket notation: {{entry.general["field-name"]}}\n' +
                            '• Genest: {{entry.linked.selectedInput}}\n' +
                            '• Conditie: {{entry.active ? "Ja" : "Nee"}}\n' +
                            '• Vergelijking: {{entry.status == "open" ? "Actief" : ""}}\n' +
                            '• Concatenatie: {{entry.field ? "Tekst: " + entry.field : ""}}\n' +
                            'Multi-line: elke regel op eigen lijn, lege regels worden verwijderd.',
                          placeholder:
                            '{{entry.status == "Defect" ? "Let op: defect" : ""}}\n{{entry.linked ? "Gekoppeld aan " + entry.linked.selectedInput : ""}}',
                          rows: 4,
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            return selectedFieldType?.value !== 'autocomplete'
                              ? true
                              : this.formService.findFieldValue(field, 'optionsType') !== 'module';
                          },
                        },
                      },
                      {
                        // Radio-only: maps a linked-module field to the option's description text.
                        key: 'descriptionFieldPath',
                        type: 'select',
                        props: {
                          label: 'Veld voor omschrijving',
                          description: 'Module-veld dat de omschrijving levert',
                          placeholder: 'Geen',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            return this.findSelectedFieldType(field)?.value !== 'custom-radio'
                              ? true
                              : this.formService.findFieldValue(field, 'optionsType') !== 'module';
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            const selectedModule = this.modulesStore
                              .modules()
                              .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));
                            if (selectedModule === undefined) {
                              return [];
                            }
                            return [
                              { value: null, label: 'Geen' },
                              ...this.formService.getFieldsOptionsOfModule(this.generatedForms()[selectedModule.id]),
                            ];
                          },
                        },
                      },
                      {
                        // Radio-only: maps a linked-module field to the option's "recommended" flag.
                        key: 'recommendedFieldPath',
                        type: 'select',
                        props: {
                          label: 'Veld voor aanbevolen',
                          description: 'Module-veld dat bepaalt of de optie aanbevolen is',
                          placeholder: 'Geen',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            return this.findSelectedFieldType(field)?.value !== 'custom-radio'
                              ? true
                              : this.formService.findFieldValue(field, 'optionsType') !== 'module';
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            const selectedModule = this.modulesStore
                              .modules()
                              .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));
                            if (selectedModule === undefined) {
                              return [];
                            }
                            return [
                              { value: null, label: 'Geen' },
                              ...this.formService.getFieldsOptionsOfModule(this.generatedForms()[selectedModule.id]),
                            ];
                          },
                        },
                      },
                      {
                        key: 'relationsInfoModuleCodes',
                        type: 'repeat',
                        props: {
                          label: 'Toon koppelingen van modules',
                          addText: 'Module toevoegen',
                          description:
                            'Toont gekoppelde entries van deze modules als extra info bij de opties (bijv. "Medewerker: Ted Voorend · Voertuig: Bus 1")',
                        },
                        fieldArray: {
                          type: 'autocomplete',
                          props: {
                            placeholder: 'Selecteer module',
                            requireSelection: false,
                            options: of([]),
                          },
                          expressions: {
                            'props.options': (field: FormlyFieldConfig) => {
                              if (field.hide) return [];
                              return this.modulesStore.enabledModules().map(m => ({
                                label: m.title,
                                value: m.code,
                              }));
                            },
                          },
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            return selectedFieldType?.value !== 'autocomplete'
                              ? true
                              : this.formService.findFieldValue(field, 'optionsType') !== 'module';
                          },
                        },
                      },

                      {
                        key: 'filters',
                        type: 'repeat',
                        props: {
                          label: 'Filter opties',
                          seperatorText: 'EN',
                          addText: 'Conditie toevoegen',
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            const moduleCode = this.formService.findFieldValue(field, 'moduleCode');
                            const optionsTypeValue = this.formService.findFieldValue(field, 'optionsType');
                            return selectedFieldType?.options ? optionsTypeValue !== 'module' || !moduleCode : true;
                          },
                        },
                        fieldArray: {
                          fieldGroup: [
                            {
                              props: { label: 'Voorwaarden' },
                              fieldGroup: [
                                {
                                  key: 'compareField',
                                  type: 'select',
                                  props: {
                                    label: 'Veld',
                                    options: [],
                                  },
                                  expressions: {
                                    'props.options': (field: FormlyFieldConfig) => {
                                      if (field.hide) {
                                        return [];
                                      }
                                      const selectedModule = this.modulesStore
                                        .modules()
                                        .find(
                                          module => module.code === this.formService.findFieldValue(field, 'moduleCode')
                                        );

                                      if (selectedModule === undefined) {
                                        return;
                                      }

                                      return this.formService.getFieldsOptionsOfModule(
                                        this.generatedForms()[selectedModule.id]
                                      );
                                    },
                                  },
                                },
                                OPERATOR_SELECT_FIELD,
                                createFieldWithOverrides(BASED_ON_SELECTED_FIELD, {
                                  expressions: {
                                    hide: field => {
                                      if (field.model?.operator === 'isnull' || field.model?.operator === '!isnull') {
                                        return true;
                                      }
                                    },
                                  },
                                }),
                                {
                                  key: 'value',
                                  type: 'input',
                                  props: {
                                    label: 'Waarde',
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) => {
                                      if (field.model?.basedOn === 'dynamic' || field.model?.operator === 'isnull') {
                                        return true;
                                      }

                                      const moduleCode = this.formService.findFieldValue(field, 'moduleCode');

                                      return (
                                        this.compareFieldHasOptions(field, moduleCode) ||
                                        this.compareFieldIsBoolean(field, moduleCode)
                                      );
                                    },
                                  },
                                },
                                {
                                  // Boolean compare-field: pick the value instead of typing it, so it
                                  // is stored as a real boolean and queried unquoted (eq true).
                                  key: 'value',
                                  type: 'custom-select',
                                  props: {
                                    label: 'Waarde',
                                    options: [
                                      { value: true, label: 'Waar' },
                                      { value: false, label: 'Niet waar' },
                                    ],
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) => {
                                      if (field.model?.basedOn === 'dynamic' || field.model?.operator === 'isnull') {
                                        return true;
                                      }

                                      const moduleCode = this.formService.findFieldValue(field, 'moduleCode');

                                      return !this.compareFieldIsBoolean(field, moduleCode);
                                    },
                                  },
                                },
                                {
                                  key: 'value',
                                  type: 'custom-select',
                                  props: {
                                    multiple: true,
                                    label: 'Waarde',
                                    options: [],
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) => {
                                      if (field.model?.basedOn === 'dynamic' || field.model?.operator === 'isnull') {
                                        return true;
                                      }

                                      const moduleCode = this.formService.findFieldValue(field, 'moduleCode');

                                      return (
                                        !this.compareFieldHasOptions(field, moduleCode) ||
                                        this.compareFieldIsBoolean(field, moduleCode)
                                      );
                                    },
                                    'props.options': (field: FormlyFieldConfig) => {
                                      if (field.hide) {
                                        return [];
                                      }
                                      const moduleCode = this.formService.findFieldValue(field, 'moduleCode');
                                      return this.getCompareFieldOptions(field, moduleCode);
                                    },
                                  },
                                },
                                {
                                  key: 'fieldKeyPath',
                                  type: 'select',
                                  props: {
                                    label: 'Waarde',
                                    options: [],
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) => {
                                      return (
                                        field.model?.basedOn === 'static' ||
                                        field.model?.operator === 'isnull' ||
                                        field.model?.operator === '!isnull'
                                      );
                                    },
                                    'props.options': (field: FormlyFieldConfig) => {
                                      if (field.hide) {
                                        return [];
                                      }

                                      return this.formService.getFieldsOptionsOfModule(this.generatedForm);
                                    },
                                  },
                                },
                                {
                                  key: 'subFieldKeyPath',
                                  type: 'select',
                                  props: {
                                    label: 'Subveld',
                                    options: [],
                                  },
                                  expressions: {
                                    hide: (field: FormlyFieldConfig) => {
                                      if (
                                        field.model?.basedOn === 'static' ||
                                        field.model?.operator === 'isnull' ||
                                        field.model?.operator === '!isnull'
                                      ) {
                                        return true;
                                      }
                                      const targetField = this.conditionFieldsService.getField(
                                        field,
                                        'fieldKeyPath',
                                        this.module().code
                                      );
                                      return !targetField?.props?.optionsArray?.some(
                                        (optionItem: OptionsItem) => optionItem.optionsType === 'module'
                                      );
                                    },
                                    'props.options': (field: FormlyFieldConfig) => {
                                      if (field.hide) {
                                        return [];
                                      }
                                      return this.subFieldKeyPathOptions(field);
                                    },
                                    'props.description': (field: FormlyFieldConfig) => {
                                      if (field.hide) {
                                        return undefined;
                                      }
                                      return FormHelper.subFieldSelectionHint(this.subFieldKeyPathOptions(field) ?? []);
                                    },
                                  },
                                },
                                {
                                  key: 'datatype',
                                  type: 'input',
                                  className: 'hidden',
                                  props: {
                                    label: 'Datatype',
                                  },
                                  expressions: {
                                    'model.datatype': (field: FormlyFieldConfig) => {
                                      // get the datatype of fieldkeyPath

                                      const selectedModule = this.modulesStore
                                        .modules()
                                        .find(
                                          module => module.code === this.formService.findFieldValue(field, 'moduleCode')
                                        );

                                      if (selectedModule === undefined) {
                                        return;
                                      }

                                      const fieldsOfTargetModule = FormHelper.getFields(
                                        this.modulesStore.generatedForms()[selectedModule.id]
                                      );

                                      const targetField = fieldsOfTargetModule.find(fieldOfTargetModule => {
                                        return (
                                          fieldOfTargetModule.props.keyPath != undefined &&
                                          fieldOfTargetModule.props.keyPath === field.model?.compareField
                                        );
                                      });
                                      if (targetField === undefined) {
                                        return;
                                      }

                                      return targetField.props.datatype;
                                    },
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      },
                      {
                        key: 'filterLogic',
                        type: 'select',
                        defaultValue: 'or',
                        props: {
                          label: 'Filter logica',
                          description:
                            'Bepaal hoe meerdere waarden gecombineerd worden: OF (één van de waarden) of EN (alle waarden)',
                          options: [
                            { label: 'OF (één van de waarden moet matchen)', value: 'or' },
                            { label: 'EN (alle waarden moeten matchen)', value: 'and' },
                          ],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            const moduleCode = this.formService.findFieldValue(field, 'moduleCode');
                            const optionsTypeValue = this.formService.findFieldValue(field, 'optionsType');
                            const filters = this.formService.findFieldValue(field, 'filters');
                            // Only show when there are filters configured
                            const hasFilters = filters && Array.isArray(filters) && filters.length > 0;
                            return selectedFieldType?.options
                              ? optionsTypeValue !== 'module' || !moduleCode || !hasFilters
                              : true;
                          },
                        },
                      },
                      {
                        key: 'checkAvailability',
                        type: 'toggle',
                        defaultValue: false,
                        props: {
                          label: 'Beschikbaarheid opvragen',
                          description:
                            'Controleer of items beschikbaar zijn op basis van een start- en einddatum en filter niet-beschikbare items uit de opties.',
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const selectedFieldType = this.findSelectedFieldType(field);
                            const optionsTypeValue = this.formService.findFieldValue(field, 'optionsType');
                            return selectedFieldType?.fetchOptions ? optionsTypeValue !== 'module' : true;
                          },
                        },
                      },
                      {
                        key: 'availabilityStartDateKeyPath',
                        type: 'select',
                        props: {
                          label: 'Start datum veld',
                          description: 'Selecteer het veld met de startdatum',
                          placeholder: 'Selecteer veld',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const checkAvailability = this.formService.findFieldValue(field, 'checkAvailability');
                            return !checkAvailability;
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            return this.formService.getFieldsOptionsOfModule(this.generatedForm);
                          },
                        },
                      },
                      {
                        key: 'availabilityEndDateKeyPath',
                        type: 'select',
                        props: {
                          label: 'Eind datum veld',
                          description: 'Selecteer het veld met de einddatum',
                          placeholder: 'Selecteer veld',
                          options: [],
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const checkAvailability = this.formService.findFieldValue(field, 'checkAvailability');
                            return !checkAvailability;
                          },
                          'props.options': (field: FormlyFieldConfig) => {
                            if (field.hide) {
                              return [];
                            }
                            return this.formService.getFieldsOptionsOfModule(this.generatedForm);
                          },
                        },
                      },
                      {
                        key: 'availabilityModuleConfigs',
                        type: 'repeat',
                        props: {
                          label: 'Module configuraties voor beschikbaarheid',
                          addText: 'Module toevoegen',
                        },
                        expressions: {
                          hide: (field: FormlyFieldConfig) => {
                            const checkAvailability = this.formService.findFieldValue(field, 'checkAvailability');
                            return !checkAvailability;
                          },
                        },
                        fieldArray: {
                          fieldGroup: [
                            {
                              key: 'moduleCode',
                              type: 'select',
                              props: {
                                label: 'Module',
                                description: 'Selecteer de module om te doorzoeken voor beschikbaarheid',
                                placeholder: 'Selecteer module',
                                options: [],
                              },
                              expressions: {
                                'props.options': () => {
                                  return this.modulesStore.modules().map(module => ({
                                    value: module.code,
                                    label: module.title,
                                  }));
                                },
                              },
                            },
                            {
                              key: 'userKeyPath',
                              type: 'input',
                              props: {
                                label: 'User/Asset ID keypath',
                                description: 'Het veld in deze module dat de user/asset ID bevat',
                                placeholder: 'entry.user.id',
                              },
                            },
                            {
                              key: 'descriptionKeyPath',
                              type: 'input',
                              props: {
                                label: 'Beschrijving keypath (optioneel)',
                                description:
                                  'Het veld dat als beschrijving getoond wordt waarom item niet beschikbaar is',
                                placeholder: 'entry.description',
                              },
                            },
                            {
                              key: 'filter',
                              type: 'input',
                              props: {
                                label: 'Filter (optioneel)',
                                description: 'OData filter expressie om entries te filteren',
                                placeholder: "entry.status eq 'closed'",
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.options;
                },
              },
            },
            {
              key: 'addText',
              type: 'input',
              props: {
                label: 'Tekst toevoegknop',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(
                    selectedFieldType?.value === 'repeat' ||
                    selectedFieldType?.value === 'linked-items' ||
                    selectedFieldType?.value === 'linkage-items' ||
                    selectedFieldType?.value === 'inline-entries' ||
                    selectedFieldType?.value === 'repeat_reorder' ||
                    selectedFieldType?.value === 'repeat_accordion' ||
                    selectedFieldType?.value === 'tags'
                  );
                },
              },
            },
            {
              key: 'syncEntry',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Synchroniseer module data',
                description: 'Synchroniseer de laatste versie van de geselecteerde entry als de hoofdentry veranderd. ',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);

                  return (
                    selectedFieldType?.value !== 'inline-entries' && selectedFieldType?.value !== 'select-linked-entry'
                  );
                },
              },
            },
            {
              key: 'initialCount',
              type: 'custom-number',
              defaultValue: 0,
              props: {
                label: 'Minimaal aantal items',
                description: 'Het minimum aantal items dat verplicht is (verwijderknop wordt verborgen)',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(
                    selectedFieldType?.value === 'inline-entries' ||
                    selectedFieldType?.value === 'repeat' ||
                    selectedFieldType?.value === 'repeat_reorder' ||
                    selectedFieldType?.value === 'repeat_accordion' ||
                    selectedFieldType?.value === 'route'
                  );
                },
              },
            },
            {
              key: 'defaultCount',
              type: 'custom-number',
              props: {
                label: 'Standaard aantal items',
                description: 'Hoeveel items standaard worden aangemaakt (kan meer zijn dan minimaal)',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(
                    selectedFieldType?.value === 'repeat' ||
                    selectedFieldType?.value === 'repeat_reorder' ||
                    selectedFieldType?.value === 'repeat_accordion' ||
                    selectedFieldType?.value === 'route'
                  );
                },
              },
            },
            {
              key: 'excludedFields',
              type: 'select',
              defaultValue: '',
              props: {
                label: 'Velden excluderen in inline entries',
                multiple: true,
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(selectedFieldType?.value === 'inline-entries');
                },
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const selectedModule = this.modulesStore
                    .modules()
                    .find(module => module.code === this.formService.findFieldValue(field, 'moduleCode'));

                  if (selectedModule === undefined) {
                    return;
                  }

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];

                  return this.formService.getFieldsOptionsOfModule(
                    this.generatedForms()[selectedModule.id],
                    excludeTypes,
                    true,
                    this.generatedForms(),
                    this.modulesStore.moduleCodeMapper()
                  );
                },
              },
            },
            {
              key: 'maxEntries',
              type: 'custom-number',
              defaultValue: 1000,
              props: {
                label: 'Maximaal aantal items',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(selectedFieldType?.value === 'inline-entries');
                },
              },
            },
            {
              key: 'moduleCode',
              type: 'input',
              defaultValue: this.module().code,
              props: {
                label: 'Module code',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(selectedFieldType?.value === 'repeat_number');
                },
              },
            },
            {
              key: 'linkedField',
              type: 'select',
              props: {
                label: 'Gelinkte invoerveld (getal)',
                options: [],
              },
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  return this.formService
                    .getFieldsOptionsOfModule(this.generatedForm)
                    .filter(field => field.type === 'custom-number');
                },
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(selectedFieldType?.value === 'repeat_number');
                },
              },
            },
            {
              key: 'seperatorText',
              type: 'input',
              props: {
                label: 'Scheidingstekst',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.repeater;
                },
              },
            },
            // enforceUploadValidator — checklist-specific toggle (reuses moduleCode + moduleLinkedField from linked-items rows above)
            {
              key: 'enforceUploadValidator',
              type: 'toggle',
              defaultValue: true,
              props: {
                label: 'Verplicht alle uploads voor save',
                description:
                  'Blokkeer form-save als niet alle vereiste documenten zijn geüpload (standaard aan voor onboarding-wizard).',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) =>
                  this.findSelectedFieldType(field)?.value !== 'required-documents-checklist',
              },
            },
            // Route configuration fields
            {
              key: 'departureDateField',
              type: 'select',
              props: {
                label: 'Vertrekdatum veld',
                description:
                  'Selecteer het datetime veld dat de vertrekdatum/tijd bevat (basis voor alle berekeningen)',
                options: [],
              },
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  return this.formService
                    .getFieldsOptionsOfModule(this.generatedForm)
                    .filter(f => f.type === 'datetime' || f.type === 'date');
                },
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'route';
                },
              },
            },
            {
              key: 'departureLocationField',
              type: 'select',
              props: {
                label: 'Vertreklocatie veld',
                description: 'Selecteer het adres/autocomplete veld voor de vertreklocatie (startpunt op de kaart)',
                options: [],
              },
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  // Custom excludeTypes without 'address' so we can select address fields
                  const excludeTypes = [
                    'pages',
                    'tabs',
                    'repeat',
                    'repeat_reorder',
                    'list',
                    'name',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];
                  return this.formService
                    .getFieldsOptionsOfModule(this.generatedForm, excludeTypes)
                    .filter(f => f.type === 'autocomplete' || f.type === 'address' || f.type === 'geolocation');
                },
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'route';
                },
              },
            },
            {
              key: 'destinationLocationField',
              type: 'select',
              props: {
                label: 'Eindbestemming veld',
                description: 'Selecteer het adres/autocomplete veld voor de eindbestemming (eindpunt op de kaart)',
                options: [],
              },
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  // Custom excludeTypes without 'address' so we can select address fields
                  const excludeTypes = [
                    'pages',
                    'tabs',
                    'repeat',
                    'repeat_reorder',
                    'list',
                    'name',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];
                  return this.formService
                    .getFieldsOptionsOfModule(this.generatedForm, excludeTypes)
                    .filter(f => f.type === 'autocomplete' || f.type === 'address' || f.type === 'geolocation');
                },
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'route';
                },
              },
            },
            {
              key: 'destinationTimeField',
              type: 'select',
              props: {
                label: 'Aankomsttijd bestemming veld',
                description: 'Selecteer het datetime/time veld voor de aankomsttijd op de eindbestemming',
                options: [],
              },
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  return this.formService
                    .getFieldsOptionsOfModule(this.generatedForm)
                    .filter(f => f.type === 'datetime' || f.type === 'time' || f.type === 'date');
                },
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'route';
                },
              },
            },
            {
              key: 'moduleFieldKey',
              type: 'select',
              props: {
                label: 'Module veld',
                description: 'Selecteer het veld dat de module bevat (module-select type)',
                placeholder: 'Selecteer veld',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'dynamic-module-autocomplete';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }
                  return this.formService
                    .getFieldsOptionsOfModule(this.generatedForm)
                    .filter(f => f.type === 'module-select');
                },
              },
            },
            {
              key: 'sourceFieldKey',
              type: 'autocomplete',
              props: {
                label: 'Bronveld',
                description: 'Selecteer het autocomplete-veld waarvan de waarde overgenomen wordt',
                placeholder: 'Selecteer veld',
                requireSelection: false,
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'linked-value';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }
                  const allOptions = this.formService.getFieldsOptionsOfModule(this.generatedForm);
                  return allOptions.filter(f => f.type === 'autocomplete' || f.type === 'dynamic-module-autocomplete');
                },
              },
            },
            {
              key: 'sourceTemplate',
              type: 'template-merge-input',
              props: {
                label: 'Bronwaarde template',
                description:
                  'Gebruik de + knop rechts om merge fields in te voegen, of typ {entry.general.fieldKey} placeholders handmatig.',
                placeholder: 'Vooraf: {entry.general.notitie-vooraf}\nAchteraf: {entry.general.notitie-achteraf}',
                rows: 6,
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'linked-value';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  if (field.hide) {
                    return [];
                  }

                  const sourceFieldKey = this.formService.findFieldValue(field, 'sourceFieldKey');
                  if (!sourceFieldKey) {
                    return [];
                  }

                  const sourceField = FormHelper.getFieldByKeyPath(sourceFieldKey, this.generatedForm);
                  if (!sourceField) return [];

                  const moduleCode = sourceField?.props?.optionsArray?.[0]?.moduleCode;
                  if (!moduleCode) return [];

                  const moduleMapper = this.modulesStore.moduleCodeMapper();
                  const sourceModule = moduleMapper?.[moduleCode];
                  if (!sourceModule?.id) return [];

                  const sourceModuleForm = this.generatedForms()[sourceModule.id];
                  if (!sourceModuleForm) return [];

                  const excludeTypes = [
                    'wizard',
                    'pages',
                    'tabs',
                    'list',
                    'linked-items',
                    'linkage-items',
                    'linkage-tags',
                    'linkage-select',
                    'required-items',
                  ];
                  return this.formService.getFieldsOptionsOfModule(sourceModuleForm, excludeTypes);
                },
              },
            },
            createFieldWithOverrides(this.valueMappingFieldService.getValueMappingFields(this.module()), {
              key: 'defaultValuesLinkedItems',
              type: 'repeat',
              props: {
                label: 'Standaard waardes voor gekoppelde items',
                addText: 'Waarde toevoegen',
                description: '',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return (
                    selectedFieldType?.value !== 'linked-items' &&
                    selectedFieldType?.value !== 'linkage-items' &&
                    selectedFieldType?.value !== 'linkage-tags' &&
                    selectedFieldType?.value !== 'linkage-select' &&
                    selectedFieldType?.value !== 'inline-entries'
                  );
                },
              },
            }),
            // readOnlyItems — linked-items alleen-lezen weergave (#20955)
            {
              key: 'readOnlyItems',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Alleen-lezen (geen toevoegen/bewerken/verwijderen)',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'linked-items',
              },
            },
            // showSearch — linked-items zoekveld boven de lijst (#20951)
            {
              key: 'showSearch',
              type: 'toggle',
              defaultValue: true,
              props: {
                label: 'Zoekveld tonen boven de lijst',
                description: `Verschijnt pas vanaf ${SEARCH_MIN_ROWS + 1} gekoppelde rijen; bij een kortere lijst blijft het zoekveld weg, ook als dit aan staat.`,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'linked-items',
              },
            },
            // sectionTitle — kop boven de gekoppelde lijst, bijv. "Prijsafspraken voor dit
            // artikel" (Figma editpagina). Leeg = geen kop (#20813-gedrag). (#20952)
            {
              key: 'sectionTitle',
              type: 'input',
              props: {
                label: 'Kop boven de lijst',
                description: 'Laat leeg als de pagina de sectie zelf al benoemt.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'linked-items',
              },
            },
            createFieldWithOverrides(this.valueMappingFieldService.getValueMappingFields(this.module()), {
              key: 'defaultValuesAutocomplete',
              type: 'repeat',
              props: {
                label: 'Standaard waardes als nieuwe item wordt aangemaakt',
                addText: 'Waarde toevoegen',
                description: '',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  const showCreateButton = this.formService.findFieldValue(field, 'showCreateButton');
                  return selectedFieldType?.value !== 'autocomplete' || !showCreateButton;
                },
              },
            }),
            {
              key: 'copyFieldsOnSelect',
              type: 'repeat',
              // The block is only authored on an autocomplete, but the RDW field reads the same
              // mappings to fill the asset category after a licence-plate lookup. Formly stamps
              // `resetOnHide: true` on every field, so without this opt-out saving an RDW field
              // wipes its mappings from sourceForm — the category then silently stops filling.
              resetOnHide: false,
              props: {
                label: 'Velden automatisch vullen bij selectie',
                addText: 'Mapping toevoegen',
                description:
                  'Wanneer een optie geselecteerd wordt, worden gekoppelde waarden automatisch ingevuld in de opgegeven doelvelden.',
                placeholder: '',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'sourceFieldPath',
                    type: 'autocomplete',
                    props: {
                      label: 'Over te nemen waarde',
                      placeholder: 'Selecteer veld uit gekoppelde module',
                      description:
                        'Het veld uit de geselecteerde module waarvan de waarde overgenomen wordt, of "Koppelingen (relations)" om uit relaties te halen',
                      required: true,
                      requireSelection: false,
                      options: of([]),
                    },
                    expressions: {
                      'props.options': (field: FormlyFieldConfig) => {
                        if (field.hide) return [];

                        const relationOption = { label: 'Koppelingen (relations)', value: 'entry.relations' };

                        // Get the optionsArray from the parent autocomplete field being configured
                        const optionsArray = this.formService.findFieldValue(field, 'optionsArray') ?? [];
                        const moduleCode = optionsArray?.[0]?.moduleCode;
                        if (!moduleCode) return [relationOption];

                        const linkedModuleId = this.modulesStore.moduleCodeToId(moduleCode);
                        if (!linkedModuleId) return [relationOption];

                        const linkedGeneratedForm = this.modulesStore.generatedForms()[linkedModuleId];
                        if (!linkedGeneratedForm) return [relationOption];

                        const fieldOptions = this.formService.getFieldsOptionsOfModule(
                          linkedGeneratedForm,
                          ['pages', 'tabs', 'list'],
                          false
                        );

                        return [relationOption, ...fieldOptions];
                      },
                    },
                  },
                  {
                    key: 'sourceRelationModuleCode',
                    type: 'autocomplete',
                    props: {
                      label: 'Relatie module',
                      placeholder: 'Selecteer de module waarmee gekoppeld is',
                      description: 'De module waarin gezocht wordt binnen de relaties van de geselecteerde entry',
                      required: true,
                      requireSelection: false,
                      options: of([]),
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        const sourceFieldPath = this.formService.findFieldValue(field, 'sourceFieldPath');
                        return sourceFieldPath !== 'entry.relations';
                      },
                      'props.options': (field: FormlyFieldConfig) => {
                        if (field.hide) return [];
                        return this.modulesStore.enabledModules().map(m => ({
                          label: m.title,
                          value: m.code,
                        }));
                      },
                    },
                  },
                  {
                    key: 'targetFieldKey',
                    type: 'autocomplete',
                    props: {
                      label: 'Doelveld',
                      placeholder: 'Selecteer invoerveld',
                      description: 'Het invoerveld in het huidige formulier dat automatisch ingevuld wordt',
                      required: true,
                      requireSelection: false,
                      options: of([]),
                    },
                    expressions: {
                      'props.options': (field: FormlyFieldConfig) => {
                        if (field.hide) return [];
                        const moduleId = this.moduleId();
                        if (!moduleId) return [];

                        const generatedForm = this.modulesStore.generatedForms()[moduleId];
                        if (!generatedForm) return [];

                        return this.formService.getFieldsOptionsOfModule(
                          generatedForm,
                          ['pages', 'tabs', 'list'],
                          false
                        );
                      },
                    },
                  },
                  createFieldWithOverrides(this.conditionFieldsService.getCondtionsField(this.module(), 'interface'), {
                    key: 'conditions',
                    type: 'repeat',
                    props: {
                      label: 'Alleen toepassen als (optioneel)',
                      description:
                        'Laat leeg om altijd toe te passen. Voeg voorwaarden toe om de mapping alleen toe te passen als de huidige entry eraan voldoet.',
                      seperatorText: 'EN',
                      addText: 'Voorwaarde toevoegen',
                    },
                  }),
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'autocomplete';
                },
              },
            },
            {
              key: 'groupFields',
              type: 'repeat_accordion',
              props: {
                label: 'Group velden',
                repeatLabel: 'Invoerveld',
                addText: 'Invoerveld toevoegen',
                seperatorText: '<hr class="my-0">',
                defaultModel: { width: 'w-full', type: 'input' },
              },
              fieldArray: {},
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'group';
                },
              },
            },
            {
              key: 'accordionFields',
              type: 'repeat_accordion',
              props: {
                label: 'Accordeon velden',
                repeatLabel: 'Invoerveld',
                addText: 'Invoerveld toevoegen',
                seperatorText: '<hr class="my-0">',
                defaultModel: { width: 'w-full', type: 'input' },
              },
              fieldArray: {},
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'accordion';
                },
              },
            },
            {
              key: 'repeatFields',
              type: 'repeat_accordion',
              props: {
                label: 'Herhaal velden',
                repeatLabel: 'Invoerveld',
                addText: 'Invoerveld toevoegen',
                seperatorText: '<hr class="my-0">',
                defaultModel: { width: 'w-full', type: 'input' },
              },
              fieldArray: {},
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.repeater;
                },
              },
            },
            {
              key: 'tabsFields',
              type: 'repeat_accordion',
              props: {
                label: 'Tabs',
                repeatLabel: 'Tab',
                addText: 'Tab toevoegen',
                seperatorText: '<hr class="my-0">',
                defaultModel: { width: 'w-full', type: 'input' },
              },
              fieldArray: {
                fieldGroup: [
                  {
                    type: 'tabs',
                    fieldGroup: [
                      {
                        props: { label: 'Algemeen' },
                        fieldGroup: [
                          {
                            key: 'label',
                            type: 'input',
                            props: {
                              label: 'Pagina naam',
                            },
                          },
                          {
                            key: 'key',
                            type: 'input',
                            props: {
                              label: 'Key',
                            },
                            expressions: {
                              'props.disabled': (field: FormlyFieldConfig) => field.model?.isOriginalField === true,
                              'props.description': (field: FormlyFieldConfig) => {
                                if (field.model?.isOriginalField === true) {
                                  return 'Dit veld is al in gebruik in de database en kan niet worden gewijzigd om dataverlies te voorkomen';
                                }
                                return 'Op basis van deze key wordt een waarde in de database gezet';
                              },
                            },
                          },
                          {
                            key: 'isOriginalField',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'hidden',
                            props: {
                              label: 'Opgeslagen in database',
                            },
                          },
                          {
                            // TODO remove this before production
                            key: 'lock',
                            type: 'toggle',
                            defaultValue: false,
                            props: {
                              label: 'Veld locken',
                              description: 'Met dit veld kan je invoervelden locken voor in de back-end',
                              placeholder: '',
                              required: false,
                            },
                          },
                          {
                            // Weergave van de vereiste-documenten-checklist. Kaarten blijft de default, zodat
                            // bestaande formulieren ongewijzigd renderen (Figma 3802:165013 voor de regels).
                            // Zelfde clone-reden als bij tagsDisplayMode hierboven: velden in een
                            // pagina/tab/wizard gebruiken deze gekopieerde editor-configuratie, en de
                            // onboarding-checklist staat áltijd in een wizard-stap.
                            key: 'documentsDisplayMode',
                            type: 'select',
                            className: 'w-max-sm',
                            props: {
                              label: 'Weergave',
                              description:
                                'Kaarten: elk vereist document krijgt een eigen kaart met statusbadge, bestandsblok ' +
                                'en een "…"-menu. Regels: compacte lijst met een scheidingslijn per regel en één knop ' +
                                'rechts — de weergave uit het onboarding-ontwerp. Let op: in de regelweergave is er ' +
                                'géén "…"-menu, dus bekijken, downloaden, archiveren en "Voorbeeld downloaden" ' +
                                'zijn daar niet bereikbaar.',
                              placeholder: '',
                              options: [
                                { value: 'cards', label: 'Kaarten' },
                                { value: 'rows', label: 'Regels' },
                              ],
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.findSelectedFieldType(field)?.value !== 'required-documents-checklist',
                            },
                          },
                          {
                            // #20806: same Weergave setting as the root-level field editor — fields
                            // nested inside pages/tabs/wizard use these cloned editor configs, so the
                            // option must exist here too or page-nested tags fields can never opt in.
                            key: 'tagsDisplayMode',
                            type: 'select',
                            className: 'w-max-sm',
                            props: {
                              label: 'Weergave',
                              description:
                                'Standaard: toevoegknop met overlay zodra het veld opties heeft (Figma). ' +
                                'Inline: altijd zichtbaar invoerveld met autocomplete (en de enige weergave zonder opties). ' +
                                'Meerdere knoppen: alle opties staan meteen zichtbaar naast elkaar — kies dit alleen bij een korte lijst met korte labels.',
                              placeholder: '',
                              options: [
                                { value: 'inline', label: 'Inline invoerveld' },
                                { value: 'menu', label: 'Eén knop met keuzelijst' },
                                { value: 'buttons', label: 'Meerdere knoppen' },
                              ],
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'tagsMenuMultiSelect',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Meerdere tegelijk kiezen (overlay blijft open)',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'maxTags',
                            type: 'input',
                            className: 'w-max-sm',
                            props: {
                              label: 'Maximaal aantal tags',
                              description: 'Leeg = geen limiet. Bij het maximum kan de gebruiker alleen nog afvinken.',
                              placeholder: '',
                              type: 'number',
                              min: 1,
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.findSelectedFieldType(field)?.value !== 'tags' ||
                                this.formService.findFieldValue(field, 'tagsDisplayMode') !== 'buttons',
                            },
                          },
                          {
                            key: 'tagsNestByGroup',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Groeperen als keuze in twee stappen',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'nestedChildrenLabel',
                            type: 'input',
                            className: 'w-max-sm',
                            props: {
                              label: 'Naam van het tweede niveau',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'tagsShowLabel',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Label boven de knop tonen',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'repeatFields',
                            type: 'repeat_accordion',
                            props: {
                              label: 'Invoervelden',
                              repeatLabel: 'Invoerveld',
                              addText: 'Invoerveld toevoegen',
                              seperatorText: '<hr class="my-0">',
                              defaultModel: { width: 'w-full', type: 'input' },
                            },
                            fieldArray: {},
                          },
                        ],
                      },
                      {
                        props: { label: 'Presentatie' },
                        fieldGroup: [
                          {
                            key: 'hideUserRole',
                            type: 'custom-multicheckbox',
                            props: {
                              label: 'Verbergen',
                              options: [],
                            },
                            expressions: {
                              'props.options': () => {
                                const userRoles = this.userRolesStore.userRoles().map(role => ({
                                  value: role.id,
                                  label: role.name,
                                }));

                                userRoles.unshift({
                                  value: 'all',
                                  label: 'Iedereen',
                                });

                                return userRoles;
                              },
                            },
                          },
                          ...cloneDeep(this.conditionalHideFields),
                          ...cloneDeep(this.conditionalDisabledFields),
                        ],
                      },
                    ],
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  return !this.findSelectedFieldType(field)?.value.includes('tabs');
                },
              },
            },
            {
              key: 'pagesFields',
              type: 'repeat_accordion',
              props: {
                label: "Pagina's",
                repeatLabel: 'pagina',
                addText: 'Pagina toevoegen',
                seperatorText: '<hr class="my-0">',
                defaultModel: { width: 'w-full', type: 'input' },
              },
              fieldArray: {
                fieldGroup: [
                  {
                    type: 'tabs',
                    fieldGroup: [
                      {
                        props: { label: 'Algemeen' },
                        fieldGroup: [
                          {
                            key: 'label',
                            type: 'input',
                            props: {
                              label: 'Pagina naam',
                            },
                          },
                          {
                            // Rendered in the 30px badge next to the title in the card header
                            // (FormlyFieldPages) — same iconpicker as the module-level icon
                            // (general.component.ts). Required per the vervangend-beleid: every
                            // page gets an icon going forward; existing configs without one keep
                            // rendering title-only as fallback until they are re-saved.
                            key: 'icon',
                            type: 'iconpicker',
                            props: {
                              label: 'Icoon',
                              required: true,
                            },
                          },
                          {
                            key: 'key',
                            type: 'input',
                            props: {
                              label: 'Key',
                            },
                            expressions: {
                              'props.disabled': (field: FormlyFieldConfig) => field.model?.isOriginalField === true,
                              'props.description': (field: FormlyFieldConfig) => {
                                if (field.model?.isOriginalField === true) {
                                  return 'Dit veld is al in gebruik in de database en kan niet worden gewijzigd om dataverlies te voorkomen';
                                }
                                return 'Op basis van deze key wordt een waarde in de database gezet';
                              },
                            },
                          },
                          {
                            // TODO remove this before production
                            key: 'lock',
                            type: 'toggle',
                            defaultValue: false,
                            props: {
                              label: 'Veld locken',
                              description: 'Met dit veld kan je invoervelden locken voor in de back-end',
                              placeholder: '',
                              required: false,
                            },
                          },
                          {
                            // Weergave van de vereiste-documenten-checklist. Kaarten blijft de default, zodat
                            // bestaande formulieren ongewijzigd renderen (Figma 3802:165013 voor de regels).
                            // Zelfde clone-reden als bij tagsDisplayMode hierboven: velden in een
                            // pagina/tab/wizard gebruiken deze gekopieerde editor-configuratie, en de
                            // onboarding-checklist staat áltijd in een wizard-stap.
                            key: 'documentsDisplayMode',
                            type: 'select',
                            className: 'w-max-sm',
                            props: {
                              label: 'Weergave',
                              description:
                                'Kaarten: elk vereist document krijgt een eigen kaart met statusbadge, bestandsblok ' +
                                'en een "…"-menu. Regels: compacte lijst met een scheidingslijn per regel en één knop ' +
                                'rechts — de weergave uit het onboarding-ontwerp. Let op: in de regelweergave is er ' +
                                'géén "…"-menu, dus bekijken, downloaden, archiveren en "Voorbeeld downloaden" ' +
                                'zijn daar niet bereikbaar.',
                              placeholder: '',
                              options: [
                                { value: 'cards', label: 'Kaarten' },
                                { value: 'rows', label: 'Regels' },
                              ],
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.findSelectedFieldType(field)?.value !== 'required-documents-checklist',
                            },
                          },
                          {
                            // #20806: same Weergave setting as the root-level field editor — fields
                            // nested inside pages/tabs/wizard use these cloned editor configs, so the
                            // option must exist here too or page-nested tags fields can never opt in.
                            key: 'tagsDisplayMode',
                            type: 'select',
                            className: 'w-max-sm',
                            props: {
                              label: 'Weergave',
                              description:
                                'Standaard: toevoegknop met overlay zodra het veld opties heeft (Figma). ' +
                                'Inline: altijd zichtbaar invoerveld met autocomplete (en de enige weergave zonder opties). ' +
                                'Meerdere knoppen: alle opties staan meteen zichtbaar naast elkaar — kies dit alleen bij een korte lijst met korte labels.',
                              placeholder: '',
                              options: [
                                { value: 'inline', label: 'Inline invoerveld' },
                                { value: 'menu', label: 'Eén knop met keuzelijst' },
                                { value: 'buttons', label: 'Meerdere knoppen' },
                              ],
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'tagsMenuMultiSelect',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Meerdere tegelijk kiezen (overlay blijft open)',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'maxTags',
                            type: 'input',
                            className: 'w-max-sm',
                            props: {
                              label: 'Maximaal aantal tags',
                              description: 'Leeg = geen limiet. Bij het maximum kan de gebruiker alleen nog afvinken.',
                              placeholder: '',
                              type: 'number',
                              min: 1,
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.findSelectedFieldType(field)?.value !== 'tags' ||
                                this.formService.findFieldValue(field, 'tagsDisplayMode') !== 'buttons',
                            },
                          },
                          {
                            key: 'tagsNestByGroup',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Groeperen als keuze in twee stappen',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'nestedChildrenLabel',
                            type: 'input',
                            className: 'w-max-sm',
                            props: {
                              label: 'Naam van het tweede niveau',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'tagsShowLabel',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Label boven de knop tonen',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'repeatFields',
                            type: 'repeat_accordion',
                            props: {
                              label: 'Invoervelden',
                              repeatLabel: 'Invoerveld',
                              addText: 'Invoerveld toevoegen',
                              seperatorText: '<hr class="my-0">',
                              defaultModel: { width: 'w-full', type: 'input' },
                            },
                            fieldArray: {},
                          },
                        ],
                      },
                      {
                        props: { label: 'Presentatie' },
                        fieldGroup: [
                          {
                            key: 'hideUserRole',
                            type: 'custom-multicheckbox',
                            props: {
                              label: 'Verbergen',
                              options: [],
                            },
                            expressions: {
                              'props.options': field => {
                                if (field.hide) {
                                  return [];
                                }
                                const userRoles = this.userRolesStore.userRoles().map(role => ({
                                  value: role.id,
                                  label: role.name,
                                }));

                                userRoles.unshift({
                                  value: 'all',
                                  label: 'Iedereen',
                                });

                                return userRoles;
                              },
                            },
                          },
                          ...cloneDeep(this.conditionalHideFields),
                          ...cloneDeep(this.conditionalDisabledFields),
                          {
                            // Card-header action button — mirrors the wizard step's "Volgende stap"
                            // interface action (same runtime props), but rendered by FormlyFieldPages
                            // as the button top-right of the header row (Figma 2652-117626).
                            // Hidden, persisted-only: LOCKED to the current module/form, same pattern
                            // as the wizard step's interfaceActionModuleCode.
                            key: 'interfaceActionModuleCode',
                            type: 'input',
                            className: 'hidden',
                            hooks: {
                              onInit: (field: FormlyFieldConfig) => {
                                if (field.formControl && this.module()?.code) {
                                  field.formControl.setValue(this.module().code);
                                }
                              },
                            },
                            props: {
                              label: 'Module voor interface actie (kaartheader knop)',
                            },
                          },
                          {
                            key: 'interfaceActionId',
                            type: 'custom-select',
                            props: {
                              label: 'Actieknop in kaartheader',
                              description:
                                'Optioneel: toont een knop rechtsboven in de kaartheader van deze pagina, gekoppeld aan een interface actie.',
                              options: [],
                            },
                            expressions: {
                              'props.options': () => {
                                const selectedModule = this.module();
                                if (!selectedModule?.interfaceActions) return [];

                                // Only actions configured for the 'Formulier button' display location.
                                return selectedModule.interfaceActions
                                  .filter(action => action.event?.type?.includes('Formulier button'))
                                  .map(action => ({
                                    value: action.id,
                                    label: action.name,
                                  }));
                              },
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'pages';
                },
              },
            },
            {
              key: 'wizardFields',
              type: 'repeat_accordion',
              props: {
                label: 'Stappenformulier',
                repeatLabel: 'Stap',
                addText: 'Stap toevoegen',
                seperatorText: '<hr class="my-0">',
                defaultModel: { width: 'w-full', type: 'input' },
              },
              fieldArray: {
                fieldGroup: [
                  {
                    type: 'tabs',
                    fieldGroup: [
                      {
                        props: { label: 'Algemeen' },
                        fieldGroup: [
                          {
                            key: 'label',
                            type: 'input',
                            props: {
                              label: 'Stap naam',
                            },
                          },
                          {
                            key: 'key',
                            type: 'input',
                            props: {
                              label: 'Key',
                            },
                            hide: this.hideIfBasisBuilder(),
                            expressions: {
                              'props.disabled': (field: FormlyFieldConfig) => field.model?.isOriginalField === true,
                              'props.description': (field: FormlyFieldConfig) => {
                                if (field.model?.isOriginalField === true) {
                                  return 'Dit veld is al in gebruik in de database en kan niet worden gewijzigd om dataverlies te voorkomen';
                                }
                                return 'Op basis van deze key wordt een waarde in de database gezet';
                              },
                            },
                          },
                          {
                            // TODO remove this before production
                            key: 'lock',
                            type: 'toggle',
                            defaultValue: false,
                            props: {
                              label: 'Veld locken',
                              description: 'Met dit veld kan je invoervelden locken voor in de back-end',
                              placeholder: '',
                              required: false,
                            },
                            hide: this.hideIfBasisBuilder(),
                          },
                          {
                            // Weergave van de vereiste-documenten-checklist. Kaarten blijft de default, zodat
                            // bestaande formulieren ongewijzigd renderen (Figma 3802:165013 voor de regels).
                            // Zelfde clone-reden als bij tagsDisplayMode hierboven: velden in een
                            // pagina/tab/wizard gebruiken deze gekopieerde editor-configuratie, en de
                            // onboarding-checklist staat áltijd in een wizard-stap.
                            key: 'documentsDisplayMode',
                            type: 'select',
                            className: 'w-max-sm',
                            props: {
                              label: 'Weergave',
                              description:
                                'Kaarten: elk vereist document krijgt een eigen kaart met statusbadge, bestandsblok ' +
                                'en een "…"-menu. Regels: compacte lijst met een scheidingslijn per regel en één knop ' +
                                'rechts — de weergave uit het onboarding-ontwerp. Let op: in de regelweergave is er ' +
                                'géén "…"-menu, dus bekijken, downloaden, archiveren en "Voorbeeld downloaden" ' +
                                'zijn daar niet bereikbaar.',
                              placeholder: '',
                              options: [
                                { value: 'cards', label: 'Kaarten' },
                                { value: 'rows', label: 'Regels' },
                              ],
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.findSelectedFieldType(field)?.value !== 'required-documents-checklist',
                            },
                          },
                          {
                            // #20806: same Weergave setting as the root-level field editor — this
                            // cloned editor config serves nested fields too, so the option must
                            // exist here or those tags fields can never opt in.
                            key: 'tagsDisplayMode',
                            type: 'select',
                            className: 'w-max-sm',
                            props: {
                              label: 'Weergave',
                              description:
                                'Standaard: toevoegknop met overlay zodra het veld opties heeft (Figma). ' +
                                'Inline: altijd zichtbaar invoerveld met autocomplete (en de enige weergave zonder opties). ' +
                                'Meerdere knoppen: alle opties staan meteen zichtbaar naast elkaar — kies dit alleen bij een korte lijst met korte labels.',
                              placeholder: '',
                              options: [
                                { value: 'inline', label: 'Inline invoerveld' },
                                { value: 'menu', label: 'Eén knop met keuzelijst' },
                                { value: 'buttons', label: 'Meerdere knoppen' },
                              ],
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.hideIfBasisBuilder() || this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'tagsMenuMultiSelect',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Meerdere tegelijk kiezen (overlay blijft open)',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'maxTags',
                            type: 'input',
                            className: 'w-max-sm',
                            props: {
                              label: 'Maximaal aantal tags',
                              description: 'Leeg = geen limiet. Bij het maximum kan de gebruiker alleen nog afvinken.',
                              placeholder: '',
                              type: 'number',
                              min: 1,
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) =>
                                this.findSelectedFieldType(field)?.value !== 'tags' ||
                                this.formService.findFieldValue(field, 'tagsDisplayMode') !== 'buttons',
                            },
                          },
                          {
                            key: 'tagsNestByGroup',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Groeperen als keuze in twee stappen',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'nestedChildrenLabel',
                            type: 'input',
                            className: 'w-max-sm',
                            props: {
                              label: 'Naam van het tweede niveau',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'tagsShowLabel',
                            type: 'toggle',
                            defaultValue: false,
                            className: 'w-max-sm',
                            props: {
                              label: 'Label boven de knop tonen',
                              placeholder: '',
                            },
                            expressions: {
                              hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'tags',
                            },
                          },
                          {
                            key: 'repeatFields',
                            type: 'repeat_accordion',
                            props: {
                              label: 'Invoervelden',
                              repeatLabel: 'Invoerveld',
                              addText: 'Invoerveld toevoegen',
                              seperatorText: '<hr class="my-0">',
                              defaultModel: { width: 'w-full', type: 'input' },
                            },
                            fieldArray: {},
                          },
                        ],
                      },
                      {
                        props: { label: 'Presentatie' },
                        fieldGroup: [
                          {
                            key: 'hideUserRole',
                            type: 'custom-multicheckbox',
                            props: {
                              label: 'Verbergen',
                              options: [],
                            },
                            expressions: {
                              'props.options': field => {
                                if (field.hide) {
                                  return [];
                                }
                                const userRoles = this.userRolesStore.userRoles().map(role => ({
                                  value: role.id,
                                  label: role.name,
                                }));

                                userRoles.unshift({
                                  value: 'all',
                                  label: 'Iedereen',
                                });

                                return userRoles;
                              },
                            },
                            hide: this.hideIfBasisBuilder(),
                          },
                          ...cloneDeep(this.conditionalHideFields),
                          ...cloneDeep(this.conditionalDisabledFields),
                          ...cloneDeep(this.nextButtonHiddenFields),
                          {
                            // Wizard step interface action — when set, the step's "Volgende stap"
                            // button runs+awaits this action (creating an entry in the target module)
                            // before advancing. Read at runtime by FormlyWizardComponent.nextStep().
                            key: 'interfaceActionModuleCode',
                            // Hidden, persisted-only: LOCKED to the current module/form. The action dropdown
                            // sources its options from this.module(), so the persisted source code must match
                            // — onInit overwrites any stale/cross-module value (e.g. an older config that
                            // pointed at a different module). A plain hidden input holds it without coupling
                            // the value to a select's option list. Read at runtime by #getStepActionModule.
                            type: 'input',
                            className: 'hidden',
                            hooks: {
                              onInit: (field: FormlyFieldConfig) => {
                                if (field.formControl && this.module()?.code) {
                                  field.formControl.setValue(this.module().code);
                                }
                              },
                            },
                            props: {
                              label: 'Module voor interface actie (volgende stap)',
                            },
                          },
                          {
                            key: 'interfaceActionId',
                            type: 'custom-select',
                            props: {
                              label: 'Interface actie (volgende stap)',
                              description:
                                'Optioneel: de actie die bij "Volgende stap" wordt uitgevoerd en afgewacht voordat de wizard verder gaat.',
                              options: [],
                            },
                            hide: this.hideIfBasisBuilder(),
                            expressions: {
                              'props.options': () => {
                                // Source the step's interface actions from the CURRENT module/form directly
                                // (the source is locked to it), so this does not depend on the hidden
                                // interfaceActionModuleCode value being populated first.
                                const selectedModule = this.module();
                                if (!selectedModule?.interfaceActions) return [];

                                // Only actions configured for the 'Formulier button' display location.
                                return selectedModule.interfaceActions
                                  .filter(action => action.event?.type?.includes('Formulier button'))
                                  .map(action => ({
                                    value: action.id,
                                    label: action.name,
                                  }));
                              },
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'wizard';
                },
              },
            },
            {
              key: 'typeName',
              type: 'input',
              defaultValue: 'input',
              className: 'hidden',
            },
          ],
        },
        {
          props: {
            label: 'Validatie',
          },
          fieldGroup: [
            {
              key: 'required',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Verplicht invoeren',
              },
            },
            {
              key: 'enableAsyncValidation',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Async validatie inschakelen',
                description:
                  'Triggert bij wijziging server-side validatie voor dit veld. Gebruik bij velden die afhankelijk zijn van backend-checks.',
              },
            },
            {
              key: 'maximalDaysRange',
              type: 'custom-number',
              className: 'inline-block mt-4',
              props: {
                label: 'Maximale aantal dagen',
                placeholder: '',
                required: false,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'date_range';
                },
              },
            },
            {
              key: 'maxLength',
              type: 'input',
              props: {
                label: 'Karakterlimiet',
                description: 'Laat leeg voor geen limiet',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType === undefined) {
                    return true;
                  }
                  return selectedFieldType.datatype[0] === 'number';
                },
              },
            },
            {
              key: 'minLength',
              type: 'input',
              props: {
                label: 'Minimaal aantal karakters',
                description: 'Laat leeg voor geen minimum aantal karakters',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType === undefined) {
                    return true;
                  }
                  return selectedFieldType.datatype[0] === 'number';
                },
              },
            },
            {
              key: 'max',
              type: 'custom-number',
              props: {
                label: 'Maximale waarde',
                description: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType === undefined) {
                    return true;
                  }
                  return selectedFieldType.datatype[0] !== 'number';
                },
              },
            },
            {
              key: 'min',
              type: 'custom-number',
              props: {
                label: 'Minimale waarde',
                description: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (selectedFieldType === undefined) {
                    return true;
                  }
                  return selectedFieldType.datatype[0] !== 'number';
                },
              },
            },
          ],
          expressions: {
            className: (field: FormlyFieldConfig) => {
              const selectedFieldType = this.findSelectedFieldTypeOfTab(field);
              return selectedFieldType?.validation ? '' : 'hidden';
            },
          },
        },
        {
          props: {
            label: 'Presentatie',
          },
          fieldGroup: [
            {
              key: 'description',
              type: 'input',
              props: {
                label: 'Instructies',
                description: 'Dit komt onder het invoerveld te staan voor extra uitleg',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (this.hideIfBasisBuilder()) {
                    return true;
                  }
                  return !selectedFieldType?.description;
                },
              },
            },
            {
              key: 'addonLeft.text',
              type: 'input',
              props: {
                label: 'Label voor invoerveld',
                description: 'Dit komt voor het invoerveld te staan',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (this.hideIfBasisBuilder()) {
                    return true;
                  }
                  return !selectedFieldType?.showUnitFields;
                },
              },
            },
            {
              key: 'addonRight.text',
              type: 'input',
              props: {
                label: 'Label na invoerveld',
                description: 'Dit komt achter het invoerveld te staan',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  if (this.hideIfBasisBuilder()) {
                    return true;
                  }
                  return !selectedFieldType?.showUnitFields;
                },
              },
            },
            {
              key: 'info',
              type: 'input',
              props: {
                label: 'Hulp tekst',
                description: 'Wordt weergegeven als tooltip op vraagteken knop',
                placeholder: '',
              },
            },
            {
              key: 'placeholder',
              type: 'input',
              props: {
                label: 'Placeholder tekst',
                description: 'Wordt weergegeven in het invoerveld',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.placeholder;
                },
              },
            },
            {
              key: 'autocomplete',
              type: 'input',
              props: {
                label: 'Autocomplete-hint',
                // The placeholder below previews `deriveAutocomplete(key)`, but that heuristic is
                // a SUGGESTION TO COPY, never a runtime fallback: `autocompleteExtension` passes
                // `key: undefined` on purpose, because guessing from an arbitrary field's key
                // produced false positives on unrelated fields that merely shared a key name.
                // The description used to promise "Leeg = afgeleid van de sleutel", so an admin
                // who left it blank trusting that got no autocomplete attribute at all.
                description:
                  'Token voor browser- en wachtwoordmanager-autofill (bijv. given-name, family-name, email, tel). Leeg = geen hint; de grijze suggestie werkt pas als je hem overneemt.',
                placeholder: '',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !selectedFieldType?.placeholder || !AUTOCOMPLETE_TYPES.has(selectedFieldType.value);
                },
                'props.placeholder': (field: FormlyFieldConfig) => {
                  const key = this.formService.findFieldValue(field, 'key');
                  return deriveAutocomplete(key) ?? 'geen';
                },
              },
            },
            {
              key: 'rows',
              type: 'input',
              defaultValue: 4,
              props: {
                label: 'Aantal rijen',
                description: 'Aantal zichtbare rijen in het tekstvak',
                placeholder: '4',
                type: 'number',
                min: 1,
                max: 20,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'textarea';
                },
              },
            },
            {
              key: 'className',
              type: 'input',
              props: {
                label: 'CSS Class',
                placeholder: 'hidden',
              },
            },
            {
              key: 'searchable',
              type: 'toggle',
              defaultValue: true,
              props: {
                label: 'Tonen in zoekresultaten',
                placeholder: 'hidden',
              },
              hide: this.hideIfBasisBuilder(),
            },
            {
              key: 'hideLabel',
              type: 'custom-toggle',
              defaultValue: 'false',
              props: {
                label: 'Verberg label',
              },
              hide: this.hideIfBasisBuilder(),
            },
            {
              key: 'hideIfValueEmpty',
              type: 'custom-toggle',
              defaultValue: false,
              props: {
                label: 'Verbergen als geen waarde heeft',
              },
              hide: this.hideIfBasisBuilder(),
            },
            {
              // #21239 task 21250/21254: puts this field on the module's optional Overzicht-tab in
              // the read view. This flag is the WHOLE opt-in — a module that has it on at least one
              // field gets the tab, one that has it nowhere gets none. The second, module-level
              // switch it used to need was dropped on 2026-09-16: `inOverview` feeds nothing else,
              // so flagging a field already IS the intent, and two switches on two screens mainly
              // produced the reading that the feature was off while it was on.
              key: 'inOverview',
              type: 'custom-toggle',
              defaultValue: false,
              props: {
                label: 'In overzicht',
                description:
                  'Toont dit veld op de Overzicht-tab van de leesweergave. De tab verschijnt zodra ' +
                  'minstens één veld van deze module is aangevinkt.',
              },
              hide: this.hideIfBasisBuilder(),
            },
            {
              // #20811: opts this select's values into badge rendering wherever it is used as a
              // table column (columns.service.ts getColumnType). Colors come from the module's
              // statusBadgeMapping (Algemeen-tab), with a fallback for the legacy
              // pending/approved/rejected/archived slugs.
              key: 'statusBadge',
              type: 'custom-toggle',
              defaultValue: false,
              props: {
                label: 'Toon als badge in kolommen',
                description: 'Rendert de waarde van dit veld als gekleurde badge-pill in tabelweergaven',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  if (this.hideIfBasisBuilder()) {
                    return true;
                  }
                  return this.findSelectedFieldType(field)?.value !== 'custom-select';
                },
              },
            },
            {
              key: 'columnOptions',
              type: 'custom-simple-multicheckbox',
              defaultValue: ['value', 'label'],
              props: {
                options: [
                  { value: 'value', label: 'Waarde' },
                  { value: 'label', label: 'Label' },
                ],
                label:
                  'Welke waarde moet getoond worden in column als gebruikt wordt als object (geavanceerde instelling)',
              },
              hide: this.hideIfBasisBuilder(),
            },
            {
              key: 'width',
              type: 'segment-buttons',
              defaultValue: 'w-full',
              props: {
                label: 'Breedte',
                description: 'Hierdoor kan je meerdere velden naast elkaar zetten',
                options: [
                  { value: 'w-1/4', label: '25%' },
                  { value: 'w-1/3', label: '33%' },
                  { value: 'w-1/2', label: '50%' },
                  { value: 'w-2/3', label: '66%' },
                  { value: 'w-3/4', label: '75%' },
                  { value: 'w-full', label: '100%' },
                ],
              },
            },
            {
              key: 'hideAddButtonForRoles',
              type: 'custom-multicheckbox',
              props: {
                label: 'Toegevoegen knop verbergen voor gebruikerrollen',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return !(
                    selectedFieldType?.value === 'inline-entries' ||
                    selectedFieldType?.value === 'repeat' ||
                    selectedFieldType?.value === 'repeat_reorder' ||
                    selectedFieldType?.value === 'repeat_accordion' ||
                    selectedFieldType?.value === 'route'
                  );
                },
                'props.options': () => {
                  const userRoles = this.userRolesStore.userRoles().map(role => ({
                    value: role.id,
                    label: role.name,
                  }));

                  userRoles.unshift({
                    value: 'all',
                    label: 'Iedereen',
                  });

                  return userRoles;
                },
              },
            },
            {
              key: 'enableDraft',
              type: 'toggle',
              defaultValue: true,
              props: {
                label: 'Concept opslaan inschakelen',
                description:
                  'Als ingeschakeld, worden ingevulde gegevens automatisch opgeslagen als concept en hersteld wanneer de gebruiker terugkeert.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'wizard';
                },
              },
            },
            {
              key: 'wizardStyle',
              type: 'select',
              defaultValue: 'standard',
              props: {
                label: 'Wizard-stijl',
                options: [
                  { value: 'standard', label: 'Standaard' },
                  { value: 'popup', label: 'Pop-up' },
                  { value: 'create-form', label: 'Aanmaakformulier' },
                ],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'wizard',
              },
            },
            {
              key: 'enableReviewStep',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Controleer-stap tonen',
                description:
                  'Voegt als laatste stap een samenvatting van alle ingevulde stappen toe, met per stap een knop om terug te gaan. Lege velden tonen "Ontbreekt" (verplicht) of "Optioneel".',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'wizard',
              },
            },
            {
              key: 'reviewConfirmText',
              type: 'textarea',
              props: {
                label: 'Bevestigingstekst controle-stap',
                description:
                  'Optioneel. Tekst bij de checkbox onderaan de controle-stap; de afrondknop wordt pas actief na aanvinken. Leeg = geen checkbox.',
                rows: 2,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) =>
                  this.findSelectedFieldType(field)?.value !== 'wizard' || field.model?.enableReviewStep !== true,
              },
            },
            {
              key: 'nonLinearCondition',
              wrappers: ['panel'],
              props: {
                label: 'Vrij invulbaar (niet-lineair)',
                description:
                  'Optioneel. Wanneer de waarde op dit veldpad gelijk is aan de opgegeven waarde, mag de gebruiker de stappen in willekeurige volgorde invullen: "Volgende stap" blokkeert niet en er wordt pas op de controle-stap volledig gevalideerd. Laat leeg voor het standaard stap-voor-stap gedrag. Het veldpad mag ook buiten dit formulier wijzen, bv. entry.user.entry.meta-data.onboarding-choice.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'wizard',
              },
              fieldGroup: [
                {
                  key: 'fieldKeyPath',
                  type: 'input',
                  props: {
                    label: 'Veldpad',
                    placeholder: 'entry.user.entry.meta-data.onboarding-choice',
                  },
                },
                {
                  key: 'value',
                  type: 'input',
                  props: {
                    label: 'Waarde',
                    placeholder: 'manual',
                  },
                },
              ],
            },
            {
              key: 'completionActions',
              type: 'repeat_reorder',
              props: {
                label: 'Afrondknoppen (laatste stap)',
                addText: 'Knop toevoegen',
                description:
                  'Knoppen onderaan de laatste stap van het aanmaakformulier. Elke knop draait een interface-actie en sluit daarna af of maakt nog een item aan.',
                forceReorder: true,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'wizard',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'label',
                    type: 'input',
                    className: 'w-1/3',
                    props: { label: 'Knoptekst', required: true },
                  },
                  {
                    key: 'interfaceActionId',
                    type: 'custom-select',
                    className: 'w-1/3',
                    props: { label: 'Interface actie', required: true, options: [] },
                    expressions: {
                      // Actions configured on THIS form-module; the wizard resolves them via
                      // formState.entity (the form-module) when no interfaceActionModuleCode is set.
                      'props.options': () => {
                        const actions = this.modulesStore.getModuleById(this.moduleId())?.interfaceActions ?? [];
                        return actions
                          .filter(action => action.event?.type?.includes('Formulier button'))
                          .map(action => ({ value: action.id, label: action.name }));
                      },
                    },
                  },
                  {
                    key: 'behavior',
                    type: 'custom-select',
                    className: 'w-1/3',
                    defaultValue: 'close',
                    props: {
                      label: 'Gedrag',
                      required: true,
                      options: [
                        { value: 'close', label: 'Afronden en sluiten' },
                        { value: 'reset', label: 'Extra toevoegen (leegmaken, open blijven)' },
                        { value: 'navigate', label: 'Afronden en navigeren' },
                      ],
                    },
                  },
                  {
                    key: 'buttonStyle',
                    type: 'custom-select',
                    className: 'w-1/3',
                    defaultValue: 'filled',
                    props: {
                      label: 'Weergave',
                      required: true,
                      options: [
                        { value: 'filled', label: 'Primair (gevuld)' },
                        { value: 'outline', label: 'Secundair (omlijnd)' },
                      ],
                    },
                  },
                  {
                    key: 'navigateTo',
                    type: 'input',
                    className: 'w-1/2',
                    defaultValue: '/onboarding',
                    props: {
                      label: 'Navigeer naar',
                      description:
                        'Route na afronden (bijv. /onboarding). Het aangemaakte item-id gaat mee als ?userId=.',
                    },
                    expressions: {
                      // Only relevant for the 'navigate' behavior (reads the sibling row value).
                      hide: (field: FormlyFieldConfig) => field.model?.['behavior'] !== 'navigate',
                    },
                  },
                  {
                    key: 'condition',
                    fieldGroupClassName: 'flex w-full gap-3',
                    fieldGroup: [
                      {
                        key: 'fieldKeyPath',
                        type: 'input',
                        className: 'w-1/2',
                        props: {
                          label: 'Toon alleen bij veld',
                          description: 'KeyPath van het formulierveld, bijv. entry.start.choice. Leeg = altijd tonen.',
                        },
                      },
                      {
                        key: 'value',
                        type: 'input',
                        className: 'w-1/2',
                        props: {
                          label: 'Toon alleen bij waarde',
                          description: 'De knop verschijnt alleen als het veld deze waarde heeft.',
                        },
                      },
                    ],
                  },
                ],
              },
            },
            {
              key: 'conditionsTargetModuleCode',
              type: 'custom-select',
              props: {
                label: 'Doelmodule voor condities',
                description:
                  'Selecteer de module waarvan de velden gebruikt worden voor conditievelden (bijv. voor "status" veld uit de personeel module).',
                options: [],
              },
              hooks: {
                onInit: (field: FormlyFieldConfig) => {
                  // Set default to current module
                  if (!field.formControl?.value) {
                    field.formControl?.setValue(this.module()?.code);
                  }
                  // Populate options with all enabled modules
                  const options = this.modulesStore.enabledModules().map(m => ({ label: m.title, value: m.code }));
                  field.props.options = options;
                },
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const selectedFieldType = this.findSelectedFieldType(field);
                  return selectedFieldType?.value !== 'wizard';
                },
              },
            },
            {
              type: 'title',
              className: 'block w-full clear-both mt-8',
              props: {
                label: 'Interface-instellingen',
              },
            },
            {
              key: 'hideUserRole',
              type: 'custom-multicheckbox',
              className: 'w-1/2 float-left',
              props: {
                label: 'Verbergen',
                options: [
                  { value: 'admin', label: 'Admin' },
                  { value: 'user', label: 'Gebruiker' },
                ],
              },
              hide: this.hideIfBasisBuilder(),
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const userRoles = this.userRolesStore.userRoles().map(role => ({
                    value: role.id,
                    label: role.name,
                  }));

                  userRoles.unshift({
                    value: 'all',
                    label: 'Iedereen',
                  });

                  return userRoles;
                },
              },
            },
            ...this.conditionalHideFields,
            {
              key: 'disableUserRole',
              type: 'custom-multicheckbox',
              className: 'w-1/2 float-left',
              props: {
                label: 'Uitschakelen',
                options: [],
              },
              expressions: {
                'props.options': field => {
                  if (field.hide) {
                    return [];
                  }
                  const userRoles = this.userRolesStore.userRoles().map(role => ({
                    value: role.id,
                    label: role.name,
                  }));

                  userRoles.unshift({
                    value: 'all',
                    label: 'Iedereen',
                  });

                  return userRoles;
                },
              },
            },
            ...cloneDeep(this.conditionalDisabledFields),
            {
              type: 'title',
              className: 'block w-full clear-both mt-8',
              props: {
                label: 'API-instellingen',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.hideRoleBasedRights(field),
              },
            },
            {
              key: 'hasRoleBasedRights',
              type: 'toggle',
              defaultValue: false,
              props: {
                label: 'Veldrechten per gebruikersrol',
                description: 'Deze rechten worden door de backend afgedwongen en gelden ook buiten dit formulier.',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => this.hideRoleBasedRights(field),
              },
            },
            {
              key: 'roleBasedRights',
              type: 'role-based-rights',
              resetOnHide: false,
              props: {
                label: 'Datatoegang',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) =>
                  this.hideRoleBasedRights(field) || field.model?.hasRoleBasedRights !== true,
              },
            },
          ],
        },
      ],
    };

    this.fieldsWithoutRepeater = [
      {
        key: 'fields',
        type: 'repeat_accordion',
        props: {
          label: 'Invoerveld',
          addText: 'Invoerveld toevoegen',
        },
        fieldArray: cloneDeep(this.formBuilderField),
      },
    ];
  }

  setFields() {
    this.fields = this.fieldsWithoutRepeater.map(field => this.optimizeFieldDefinition(field));

    const readMode = !this.canMutate();
    if (readMode) {
      // The stamp below writes INTO the nested tree, and `optimizeFieldDefinition` only shallow-
      // copies: without this clone the read branch would leave `disabled: true` behind on
      // `fieldsWithoutRepeater`, so a later rebuild in edit mode would inherit a dead editor.
      this.fields[0] = { ...this.fields[0], fieldArray: cloneDeep(this.fields[0].fieldArray) };
    }

    this.fieldProcessorService.processFormFields(
      this.fields[0].fieldArray['fieldGroup'][0]['fieldGroup'],
      this.formBuilderField
    );

    if (readMode) {
      // Read mode (#21239) stamps `props.disabled` on this OWN-VIEW type, but the builder renders
      // its own nested `<formly-form>` — a SECOND field tree that `markLeavesReadonly` never walks
      // (it only sees the page's fields), so out there the stamp is a no-op.
      //
      // Stamping the nested ROOT alone is not enough either, which is why the whole subtree is
      // walked: `FieldFormExtension.setValidators` carries a disabled ancestor down to the leaf
      // FormControls (ngx-formly-core.mjs:1347-1362) but writes `props.disabled` on NOBODY, and
      // `props.disabled` is exactly what every repeat family reads to decide whether to draw its
      // Toevoegen / Dupliceren / Verwijderen buttons (repeat-accordion.component.ts:238). So the
      // NESTED lists (groupFields, accordionFields, the pages/wizard/options lists) kept offering
      // live mutation the moment a reader expanded a field.
      //
      // Runs AFTER `processFormFields`, because that is what materialises the nested `fieldArray`s:
      // a stamp before it would miss every list it fills in.
      //
      // Drag reads neither prop, so the template disables the CDK drop-list group instead:
      // `CdkDropList.disabled` inherits from its group and `CdkDrag.disabled` from its drop
      // container, so one binding stops every reorder in the builder.
      this.#disableSubtree(this.fields);
    }
  }

  /**
   * Stamps `props.disabled` on every node of a field tree, `fieldArray` templates included.
   *
   * Deliberately NOT `markLeavesReadonly` (`readonly/readonly.types.ts`): that helper also stamps
   * `props.readonly`, and `readonlyExtension` turns a read-only field into a label/value row.
   * These are the builder's own CONFIG inputs (field type, label, options), not entry answers, so
   * read rows here would be a different — and much larger — change than switching the editor off.
   *
   * `props` is replaced rather than mutated: the root's `props` object is still shared with
   * `fieldsWithoutRepeater` (`optimizeFieldDefinition` shallow-copies the field, not its props).
   */
  #disableSubtree(fields: FormlyFieldConfig[] | undefined): void {
    for (const field of fields ?? []) {
      field.props = { ...field.props, disabled: true };
      this.#disableSubtree(field.fieldGroup);
      // `fieldArray` is a config OR a factory function; only a config can be stamped up front, and
      // a config is the shape every repeat in this builder stores.
      const fieldArray = field.fieldArray;
      if (fieldArray && typeof fieldArray !== 'function') {
        this.#disableSubtree([fieldArray]);
      }
    }
  }

  private optimizeFieldDefinition(field: FormlyFieldConfig): FormlyFieldConfig {
    // Remove unnecessary properties not needed for rendering
    const optimizedField = { ...field };

    // Remove expressions that are not active
    if (optimizedField.expressions) {
      Object.keys(optimizedField.expressions).forEach(key => {
        if (optimizedField.hide || !optimizedField.expressions[key]) {
          delete optimizedField.expressions[key];
        }
      });
    }

    // Remove props that are not used
    if (optimizedField.props) {
      const usedProps = ['label', 'required', 'options']; // etc
      Object.keys(optimizedField.props).forEach(key => {
        if (!usedProps.includes(key)) {
          delete optimizedField.props[key];
        }
      });
    }

    return optimizedField;
  }

  fetchRequiredEntries() {
    const moduleData = this.filtersService.getModuleDataToFetch(
      this.modulesStore.generatedForms()[this.module().id],
      this.modulesStore.moduleCodeMapper()
    );
    moduleData.forEach(moduleData => {
      this.modulesStore.loadEntries({
        moduleId: moduleData.moduleId,
        applyQueryParams: false,
        limit: 5000,
        offset: 0,
      });
    });
  }

  /**
   * Creates the nextButtonConditions field configuration.
   * The condition field expressions automatically check for `conditionsTargetModuleCode`
   * in the root form to determine which module's fields to use.
   */
  #createNextButtonConditionsField(): FormlyFieldConfig {
    // Get condition field config - the expressions inside will automatically
    // use getEffectiveModuleCode to check for conditionsTargetModuleCode override
    const conditionConfig = this.conditionFieldsService.getCondtionsField(this.module(), 'interface');

    return {
      key: 'nextButtonConditions',
      type: 'repeat',
      props: {
        label: 'Voorwaardes',
        seperatorText: 'EN',
        addText: 'Conditie toevoegen',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          return !field.parent?.form?.get('nextButtonConditional')?.value;
        },
      },
      fieldArray: conditionConfig.fieldArray,
    };
  }

  findSelectedFieldTypeOfTab(field) {
    return this.fieldTypes.find(fieldType => {
      return field.parent.fieldGroup[0].fieldGroup[0].model?.type === fieldType.value;
    });
  }

  findSelectedFieldType(currentField: FormlyFieldConfig) {
    let index = 0;
    while (currentField) {
      if (currentField.parent && currentField.parent.form.get('type')) {
        index += 1;
        return this.fieldTypes.find(fieldType => {
          return currentField.parent.form.get('type').value === fieldType.value;
        });
      }
      currentField = currentField.parent;
    }
    return undefined;
  }

  /**
   * Options of the eigenschap selected in the same optionsArray item, as
   * slug/label pairs for the include/exclude picker. Resolves the owning module
   * the same way the `taxonomyId` select does (current module unless the admin
   * picked an external one).
   */
  taxonomyEntryOptions(field: FormlyFieldConfig): Array<{ value: string; label: string }> {
    const taxonomyId = this.formService.findFieldValue(field, 'taxonomyId');
    if (!taxonomyId) {
      return [];
    }
    const selectedModuleCode = this.formService.findFieldValue(field, 'moduleCode') ?? this.module().code;
    const selectedModule = this.modulesStore.modules().find(module => module.code === selectedModuleCode);
    const sourceModuleId = selectedModule?.id ?? this.module().id;
    const taxonomy = this.modulesStore.taxonomies()[sourceModuleId]?.find(item => item.id === taxonomyId);
    return (taxonomy?.entries ?? []).map(entry => ({ value: entry.slug, label: entry.name ?? entry.slug }));
  }

  compareFieldHasOptions(field: FormlyFieldConfig, moduleCode: string) {
    const compareFieldValue = this.formService.findFieldValue(field, 'compareField');
    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    const moduleField = FormHelper.getFieldByKeyPath(
      compareFieldValue,
      this.modulesStore.generatedForms()[selectedModule.id]
    );
    return !!moduleField?.props.optionsArray;
  }

  /**
   * True when the field picked in a filter row's `compareField` is a boolean
   * (formly `toggle`). Those get a Waar/Niet waar picker instead of a free-text
   * value, so the stored value is a real boolean and the query stays unquoted.
   */
  compareFieldIsBoolean(field: FormlyFieldConfig, moduleCode: string) {
    const compareFieldValue = this.formService.findFieldValue(field, 'compareField');
    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    if (!compareFieldValue || selectedModule === undefined) {
      return false;
    }
    const moduleField = FormHelper.getFieldByKeyPath(
      compareFieldValue,
      this.modulesStore.generatedForms()[selectedModule.id]
    );
    return moduleField?.props?.datatype?.[0] === 'boolean';
  }

  /**
   * Memoized options for the "Subveld" (`subFieldKeyPath`) select — same pattern as the
   * condition subfield selects in `ConditionFieldsService.subFieldOptions`: `props.options` and
   * `props.description` both call this, the memo makes the second call free, and the stored
   * value survives a renamed subfield or an unavailable linked module instead of rendering blank.
   */
  subFieldKeyPathOptions(field: FormlyFieldConfig): FieldOption[] | undefined {
    const storedValue = field.model?.subFieldKeyPath;
    return FormHelper.memoizedFieldOptions(
      field,
      [
        field.model?.fieldKeyPath,
        storedValue,
        this.module().code,
        this.modulesStore.modules(),
        this.modulesStore.generatedForms(),
      ],
      () =>
        FormHelper.moduleLinkedFieldOptions(
          this.conditionFieldsService.getField(field, 'fieldKeyPath', this.module().code),
          'Hele object vergelijken',
          this.modulesStore,
          this.formService,
          { logUnresolvedModuleCode: true, storedValue }
        )
    );
  }

  getCompareFieldOptions(field: FormlyFieldConfig, moduleCode: string) {
    const compareFieldValue = this.formService.findFieldValue(field, 'compareField');
    const options = [{ value: 'currentValue', label: 'Geselecteerde waarde (dynamisch)' }];

    if (!compareFieldValue) return of(options);

    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    const moduleField = FormHelper.getFieldByKeyPath(
      compareFieldValue,
      this.modulesStore.generatedForms()[selectedModule.id]
    );

    if (!moduleField?.props.optionsArray) return of(options);

    return this.getOptionsOfModuleField(moduleField, field.id);
  }

  getOptionsOfModuleField(moduleField: FormlyFieldConfig, cacheId: string) {
    const moduleFieldTypeIsTaxonomyOrManual = moduleField.props.optionsArray.some(
      optionsItem => optionsItem.optionsType === 'taxonomy' || optionsItem.optionsType === 'manual'
    );
    if (moduleFieldTypeIsTaxonomyOrManual) {
      const options = [];
      moduleField.props.optionsArray.forEach(optionsItem => {
        if (optionsItem.optionsType === 'manual') {
          options.push(...this.formService.generateManualOptions(optionsItem, false));
          return;
        }

        const moduleId = this.modulesStore.moduleCodeToId(optionsItem.moduleCode);

        if (moduleId == null) {
          return;
        }

        this.modulesStore.taxonomies()[moduleId]?.forEach(taxonomy => {
          if (taxonomy.id === optionsItem.taxonomyId) {
            taxonomy.entries.forEach(entry => {
              options.push({
                value: entry,
                label: entry.selectedInput,
              });
            });
          }
        });
      });
      return options;
    }

    const moduleData = moduleField.props.optionsArray.map(optionsItem => {
      const moduleId = this.modulesStore.getModuleByCode(optionsItem.moduleCode).id;
      const entry = { id: undefined, entry: {} } as Entry;
      const filter = optionsItem.filters
        ? this.filtersService.filtersToFilterString(
            optionsItem.filters,
            entry,
            undefined,
            optionsItem.filterLogic ?? 'or'
          )
        : '';
      return { moduleId, filter, keyPath: optionsItem.moduleLinkedField };
    });

    const requests = moduleData.map(data => {
      if (this.tempEntries[cacheId] && this.tempEntries[cacheId][data.moduleId]) {
        return of(this.tempEntries[cacheId][data.moduleId]);
      }
      return this.entriesService.getEntries({
        moduleId: data.moduleId,
        limit: 5000,
        offset: 0,
        filter: data.filter,
      });
    });

    return forkJoin(requests).pipe(
      tap((responses: Array<any>) => {
        responses.forEach((entries, index) => {
          const moduleId = moduleData[index].moduleId;
          if (this.tempEntries[cacheId] === undefined) this.tempEntries[cacheId] = {};
          this.tempEntries[cacheId][moduleId] = entries;
        });
      }),
      map(responses => {
        let options = [];
        responses.forEach((respones, index) => {
          const keyPath = moduleData[index].keyPath;
          const optionsFormat = respones.map(entry => {
            return {
              value: entry,
              label: UtilityService.keyPathToValue(entry, keyPath),
            };
          });
          options = [...options, ...optionsFormat];
        });
        return options;
      })
    );
  }

  hideIfBasisBuilder() {
    return this.field.props?.formbuilderSettings === 'basic';
  }

  private hideRoleBasedRights(field: FormlyFieldConfig): boolean {
    const selectedFieldType = this.findSelectedFieldType(field);
    return this.hideIfBasisBuilder() || selectedFieldType?.datatype?.includes('undefined') === true;
  }

  /** Picker for the Documentrecords interface action a review decision runs on its documents. */
  #reviewDocumentActionField(key: string, label: string, description: string): FormlyFieldConfig {
    return {
      key,
      type: 'custom-select',
      className: 'w-max-sm',
      props: { label, description, options: [] },
      expressions: {
        hide: (field: FormlyFieldConfig) => this.findSelectedFieldType(field)?.value !== 'readonly_onboarding',
        'props.options': () =>
          (this.modulesStore.getModuleByCode('document-records')?.interfaceActions ?? []).map(action => ({
            value: action.id,
            label: action.name,
          })),
      },
    };
  }
}
